'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  BaseEdge,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Building2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Maximize2,
  User,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  CompanyBasicResponse,
  CompanyNetworkResponse,
  NetworkNode,
  PersonPageResponse,
} from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { elkLastMode, elkLayout } from '@/lib/elk-layout';
import { freezeBreadcrumb, reportFreezeEvent } from '@/lib/freeze-reporter';
import { formatSharePercent } from '@/lib/helpers';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Slider, SliderThumb } from '@/components/ui/slider';
import { LocaleLink } from '@/components/locale-link';

// ---------------------------------------------------------------------------
// Custom nodes — invisible handles required by ReactFlow for edge connections
// ---------------------------------------------------------------------------
const HIDDEN_HANDLE: React.CSSProperties = {
  opacity: 0,
  width: 0,
  height: 0,
  minWidth: 0,
  minHeight: 0,
  border: 'none',
  padding: 0,
};

// Per-node sizing. Each node fits its label width instead of a fixed 280px,
// so short labels (e.g. "Paul Rossol") render snug. Labels that would exceed
// 280px on one line are kept at 280px and the height bumps to 56px so the
// 2-line wrap (line-clamp-2) is fully visible.
const NODE_MIN_WIDTH = 120;
const NODE_MAX_WIDTH = 280;
const NODE_HEIGHT_ONE_LINE = 44;
const NODE_HEIGHT_TWO_LINES = 56;
// 10px left padding + 24px icon + 6px gap + 10px right padding.
const NODE_CHROME_WIDTH = 10 + 24 + 6 + 10;
// CompanyNode adds a 36px ExternalLink button preceded by a 6px gap. The
// link has -mr-1 (-4px) trimming the right padding, so the net horizontal
// contribution is 6 (gap) + 36 (link) - 4 (negative margin) = 38px.
const COMPANY_EXT_LINK_WIDTH = 38;

let textMeasureCanvas: HTMLCanvasElement | null = null;
function measureLabelWidth(text: string, weight: 500 | 600): number {
  if (typeof document === 'undefined') return text.length * 7;
  if (!textMeasureCanvas) textMeasureCanvas = document.createElement('canvas');
  const ctx = textMeasureCanvas.getContext('2d');
  if (!ctx) return text.length * 7;
  ctx.font = `${weight} 14px Inter, system-ui, -apple-system, sans-serif`;
  return ctx.measureText(text).width;
}

function computeNodeSize(
  label: string,
  nodeType: 'center' | 'company' | 'person',
  hasExternalLink: boolean,
): { width: number; height: number } {
  const weight = nodeType === 'center' ? 600 : 500;
  const textW = measureLabelWidth(label, weight);
  const oneLine =
    NODE_CHROME_WIDTH +
    Math.ceil(textW) +
    2 + // 1px safety on each side to avoid mid-char clipping
    (hasExternalLink ? COMPANY_EXT_LINK_WIDTH : 0);
  if (oneLine <= NODE_MAX_WIDTH) {
    return {
      width: Math.max(NODE_MIN_WIDTH, oneLine),
      height: NODE_HEIGHT_ONE_LINE,
    };
  }
  return { width: NODE_MAX_WIDTH, height: NODE_HEIGHT_TWO_LINES };
}

function CenterNode({ data }: NodeProps) {
  const faviconUrl = data.faviconUrl as string | undefined;
  const width = (data.width as number | undefined) ?? NODE_MAX_WIDTH;
  const height = (data.height as number | undefined) ?? NODE_HEIGHT_ONE_LINE;
  return (
    <div
      className="flex items-center gap-1.5 rounded-lg px-2.5 py-2"
      style={{
        width,
        height,
        background: 'color-mix(in srgb, var(--fd-accent) 14%, var(--fd-card))',
        color: 'var(--fd-ink)',
        border: '1.5px solid var(--fd-accent-ink)',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={HIDDEN_HANDLE}
      />
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full overflow-hidden"
        style={{ background: 'var(--fd-card)', color: 'var(--fd-accent-ink)' }}
      >
        {faviconUrl ? (
          <img
            src={faviconUrl}
            alt={`${(data.label as string) ?? ''} Logo`}
            className="h-4 w-4 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <Building2 size={13} />
        )}
      </div>
      <span className="min-w-0 text-sm font-semibold leading-tight line-clamp-2">
        {data.label as string}
      </span>
    </div>
  );
}

function PersonNode({ data }: NodeProps) {
  const width = (data.width as number | undefined) ?? NODE_MAX_WIDTH;
  const height = (data.height as number | undefined) ?? NODE_HEIGHT_ONE_LINE;
  return (
    <div
      className="flex items-center gap-1.5 rounded-lg px-2.5 py-2"
      style={{
        width,
        height,
        background: 'color-mix(in srgb, var(--fd-good) 10%, var(--fd-card))',
        color: 'var(--fd-ink)',
        border:
          '1px solid color-mix(in srgb, var(--fd-good) 35%, var(--fd-line-2))',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={HIDDEN_HANDLE}
      />
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'var(--fd-card)', color: 'var(--fd-good)' }}
      >
        <User size={13} />
      </div>
      <span className="min-w-0 text-sm font-medium leading-tight line-clamp-2">
        {data.label as string}
      </span>
    </div>
  );
}

function CompanyNode({ data }: NodeProps) {
  const { locale } = useLocale();
  const width = (data.width as number | undefined) ?? NODE_MAX_WIDTH;
  const height = (data.height as number | undefined) ?? NODE_HEIGHT_ONE_LINE;
  return (
    <div
      className="flex items-center gap-1.5 rounded-lg px-2.5 py-2"
      style={{
        width,
        height,
        background: 'var(--fd-card)',
        color: 'var(--fd-ink)',
        border: '1px solid var(--fd-line-2)',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={HIDDEN_HANDLE}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={HIDDEN_HANDLE}
      />
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'var(--fd-bg-2)', color: 'var(--fd-ink-2)' }}
      >
        <Building2 size={13} />
      </div>
      <span className="min-w-0 text-sm font-medium leading-tight line-clamp-2">
        {data.label as string}
      </span>
      {!!data.eu_id && !!data.register_court && (
        <a
          href={generateCompanySlug(
            {
              name: data.label as string,
              registerCourt: data.register_court as string,
              registerType: (data.register_type as string) ?? undefined,
              registerId: (data.register_id as string) ?? undefined,
            },
            locale,
          )}
          onClick={(e) => e.stopPropagation()}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded hover:bg-black/10 transition-colors touch-manipulation -mr-1"
          title="Open company"
        >
          <ExternalLink className="h-full w-full p-1.5" />
        </a>
      )}
    </div>
  );
}

const NODE_TYPES = {
  center: CenterNode,
  person: PersonNode,
  company: CompanyNode,
};

// ---------------------------------------------------------------------------
// Custom orthogonal edge — renders ELK's routed path with right-angle bends
// ---------------------------------------------------------------------------
function OrthogonalEdge({ id, data, style, markerEnd, label }: EdgeProps) {
  const path = (data?.elkPath as string) || '';

  // Compute label position at the midpoint of the path
  const points = (data?.elkPoints as { x: number; y: number }[]) || [];
  let labelX = 0;
  let labelY = 0;
  // Track the segment the label sits on so the merged-label overlay can
  // decide whether to wrap: vertical segment → wrap onto two lines; horizontal
  // segment → keep on one line so the label runs alongside the edge.
  let labelSegmentHorizontal = false;
  if (points.length >= 2) {
    // Walk segments to find the point at half the total length
    const segs: number[] = [];
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      segs.push(len);
      total += len;
    }
    let half = total / 2;
    for (let i = 0; i < segs.length; i++) {
      if (half <= segs[i]) {
        const t = segs[i] > 0 ? half / segs[i] : 0;
        labelX = points[i].x + t * (points[i + 1].x - points[i].x);
        labelY = points[i].y + t * (points[i + 1].y - points[i].y);
        const dx = Math.abs(points[i + 1].x - points[i].x);
        const dy = Math.abs(points[i + 1].y - points[i].y);
        labelSegmentHorizontal = dx >= dy;
        break;
      }
      half -= segs[i];
    }
  }

  // Merged (Gesellschafter + Geschäftsführer in one) edges render the long
  // combined label via EdgeLabelRenderer so it wraps. Single-role edges keep
  // BaseEdge's SVG label.
  const isMerged = !!data?.isMerged;
  const hasLabel = label != null && label !== '' && points.length >= 2;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={style}
        markerEnd={markerEnd}
        label={isMerged ? undefined : label}
        labelX={labelX}
        labelY={labelY}
        labelStyle={{ fontSize: 10, fill: 'var(--fd-muted)' }}
        labelShowBg={true}
        labelBgStyle={{ fill: 'var(--fd-card)', fillOpacity: 1 }}
        labelBgPadding={[2, 3] as [number, number]}
        labelBgBorderRadius={3}
      />
      {isMerged && hasLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: 'var(--fd-card)',
              color: 'var(--fd-muted)',
              fontSize: 10,
              padding: '2px 4px',
              borderRadius: 3,
              lineHeight: 1.25,
              textAlign: 'center',
              pointerEvents: 'none',
              ...(labelSegmentHorizontal
                ? { whiteSpace: 'nowrap' }
                : { whiteSpace: 'normal', maxWidth: 120 }),
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const EDGE_TYPES = {
  orthogonal: OrthogonalEdge,
};

// ---------------------------------------------------------------------------
// Edge direction normalisation
// Backend convention: source = role-bearer (owner, manager, parent), target =
// subject (the company the role is in). In ELK's DOWN layout the source ends
// up ABOVE the target, so the natural direction already places owners and
// managers above their companies and subsidiaries below the focal that owns
// them. The only fix we apply is when the backend hands us a person on the
// target side of an upstream/downstream role: swap so the person sits above.
// ---------------------------------------------------------------------------
const UPSTREAM_ROLES = [
  'gesellschafter',
  'kommanditist',
  'aktionär',
  'aktionaer',
  'anteilseigner',
  'inhaber',
  'shareholder',
  'alleingesellschafter',
  'mehrheitsgesellschafter',
  'minderheitsgesellschafter',
  'stiller gesellschafter',
  'beteiligung',
  'muttergesellschaft',
  'muttergesellschaft',
  'holding',
  'konzernmutter',
  'parent',
  'parent company',
];

const DOWNSTREAM_ROLES = [
  'vorstand',
  'geschäftsführer',
  'geschaeftsfuehrer',
  'prokurist',
  'prokura',
  'direktor',
  'aufsichtsrat',
  'beirat',
  'liquidator',
  'abwickler',
  'geschäftsführerin',
  'vorstandsvorsitzender',
  'generaldirektor',
  'ceo',
  'cfo',
  'cto',
  'coo',
];

// Roles where the backend uses an INVERTED convention: source = the
// subordinate entity (subsidiary), target = the parent. Edges like these
// arrive with the subsidiary as source, so in ELK's DOWN layout the
// subsidiary would land above the parent. We flip them so the parent
// (target) ends up above the subsidiary.
const SUBSIDIARY_ROLES = [
  'tochter',
  'tochtergesellschaft',
  'tochterunternehmen',
  'subsidiary',
  'subsidiary company',
  'zweigniederlassung',
];

function normaliseEdges(
  edges: import('@/lib/backend-api').NetworkEdge[],
  nodeMap: Map<string, NetworkNode>,
): import('@/lib/backend-api').NetworkEdge[] {
  return edges.map((e) => {
    const role = (e.role ?? '').toLowerCase();
    const isUpstream = isUpstreamRole(role);
    const isDownstream = isManagingRole(role);
    const isSubsidiary = SUBSIDIARY_ROLES.some((r) => role.includes(r));

    // Subsidiary roles arrive as (subsidiary, parent) — flip so the parent
    // is the source and the subsidiary sits below it in ELK's DOWN layout.
    if (isSubsidiary) {
      return { ...e, source: e.target, target: e.source };
    }

    if (!isUpstream && !isDownstream) return e;

    // Defensive: backend almost always returns (role-bearer, company). When
    // it doesn't — i.e., a person sits on the target side of an owner/manager
    // role — swap so the person ends up as source (above the company).
    const srcType = nodeMap.get(e.source)?.node_type;
    const tgtType = nodeMap.get(e.target)?.node_type;
    if (srcType === 'company' && tgtType === 'person') {
      return { ...e, source: e.target, target: e.source };
    }
    return e;
  });
}

function isSubsidiaryRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  return SUBSIDIARY_ROLES.some((x) => r.includes(x));
}

// "Persönlich haftende(r) Gesellschafter(in)" / Komplementär is the general
// partner of a KG — typically a liability-shield entity that MANAGES the company
// without a real ownership stake. It contains the word "Gesellschafter" but must
// be classified as a managing role, never as ownership.
function isManagingRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  return (
    DOWNSTREAM_ROLES.some((x) => r.includes(x)) ||
    r.includes('persönlich haftend') ||
    r.includes('persoenlich haftend') ||
    r.includes('komplement')
  );
}

function isUpstreamRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  if (isManagingRole(r)) return false; // Komplementär / persönlich haftend
  return UPSTREAM_ROLES.some((x) => r.includes(x));
}

/**
 * Ids of the full UBO (ultimate beneficial owner) chain above the focal company
 * — its owners, their owners, and so on all the way up (parent of parents of
 * parents). Walks RAW edges via the upstream-ownership convention (source =
 * owner, target = owned). These nodes are always shown, regardless of the
 * depth or per-hub caps. The focal id itself is included as the chain root.
 */
function uboChainIds(
  edges: import('@/lib/backend-api').NetworkEdge[],
  focalId: string,
): Set<string> {
  const ownersByOwned = new Map<
    string,
    { owner: string; share: number | null }[]
  >();
  for (const e of edges) {
    if (!isUpstreamRole(e.role)) continue;
    // owner = source, owned = target
    if (!ownersByOwned.has(e.target)) ownersByOwned.set(e.target, []);
    ownersByOwned
      .get(e.target)!
      .push({ owner: e.source, share: e.share_percent ?? null });
  }
  const chain = new Set<string>([focalId]);
  const queue = [focalId];
  while (queue.length) {
    const cur = queue.shift()!;
    const owners = ownersByOwned.get(cur) ?? [];
    // Follow only the CONTROLLING owner(s) — the largest known stake — so a wide
    // co-investor cap table (e.g. 40 VCs each holding < 5%) isn't all treated as
    // "the parent chain" and exempted from the depth cap. When no share is known
    // we can't tell who controls, so follow all.
    const shares = owners
      .map((o) => o.share)
      .filter((s): s is number => s != null);
    const follow =
      shares.length > 0
        ? owners.filter((o) => o.share === Math.max(...shares))
        : owners;
    for (const { owner } of follow) {
      if (!chain.has(owner)) {
        chain.add(owner);
        queue.push(owner);
      }
    }
  }
  return chain;
}

/**
 * Default depth for the slider, computed over the set of nodes that will
 * actually be displayed (already role-filtered and per-hub-capped). When fewer
 * than 30 nodes would show it returns the deepest hop actually present — a
 * finite bound, NOT "show all" — so that toggling filters/owners later cannot
 * pull in deeper structure (a co-investor's owners, a fund-of-funds, …) and
 * blow the graph up. For larger sets it returns the hop depth whose visible
 * count is closest to 30. UBO-chain nodes always count as visible since they're
 * exempt from the depth cap.
 */
function defaultDepthForSet(
  nodes: NetworkNode[],
  edges: import('@/lib/backend-api').NetworkEdge[],
  uboChain: Set<string>,
  focalId: string,
): number | null {
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!adj.has(e.source)) adj.set(e.source, []);
    if (!adj.has(e.target)) adj.set(e.target, []);
    adj.get(e.source)!.push(e.target);
    adj.get(e.target)!.push(e.source);
  }
  const depth = new Map<string, number>([[focalId, 0]]);
  const q = [focalId];
  while (q.length) {
    const c = q.shift()!;
    const d = depth.get(c)!;
    for (const nb of adj.get(c) ?? []) {
      if (!depth.has(nb)) {
        depth.set(nb, d + 1);
        q.push(nb);
      }
    }
  }
  const maxD = Math.max(0, ...depth.values());
  // Small graph: bound at the deepest hop present (null only when there is
  // nothing beyond the focal, where there is nothing to cap anyway).
  if (nodes.length < 30) return maxD > 0 ? maxD : null;
  let best = 1;
  let bestDiff = Infinity;
  for (let d = 1; d <= maxD; d++) {
    let count = 0;
    for (const n of nodes) {
      const dp = depth.get(n.id);
      if ((dp !== undefined && dp <= d) || uboChain.has(n.id)) count++;
    }
    const diff = Math.abs(count - 30);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = d;
    }
  }
  return best;
}

/**
 * Node ids of the focal company's direct subsidiaries (Tochterfirmen). Works on
 * RAW (un-normalised) edges: the backend's inverted convention puts the
 * subsidiary on the source side and the parent on the target side, but we
 * accept either orientation defensively.
 */
function directSubsidiaryIds(
  edges: import('@/lib/backend-api').NetworkEdge[],
  focalId: string,
): string[] {
  const ids = new Set<string>();
  for (const e of edges) {
    if (!isSubsidiaryRole(e.role)) continue;
    if (e.target === focalId) ids.add(e.source);
    else if (e.source === focalId) ids.add(e.target);
  }
  return [...ids];
}

/**
 * Groups of companies that "fan out" from a single hub and would clutter the
 * graph when a hub has many of them. Two kinds of hub:
 *   1. the focal company → its direct subsidiaries (Tochterfirmen);
 *   2. every person → the OTHER companies they're tied to (e.g. a CEO who runs
 *      20 companies besides the focal).
 *
 * A person's fan-out is detected by undirected BFS depth from the focal: any
 * company-neighbour of the person that sits deeper than the person is one of
 * "their" companies (the shallower one is how the person entered the graph).
 * Each group is returned as { hubId, companyIds } so callers can cap per hub.
 */
function companyBranchGroups(
  nodes: NetworkNode[],
  edges: import('@/lib/backend-api').NetworkEdge[],
  focalId: string,
): { hubId: string; companyIds: string[] }[] {
  const typeById = new Map(nodes.map((n) => [n.id, n.node_type]));
  const adj = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.id, []);
  for (const e of edges) {
    adj.get(e.source)?.push(e.target);
    adj.get(e.target)?.push(e.source);
  }
  const depth = new Map<string, number>([[focalId, 0]]);
  const q = [focalId];
  while (q.length) {
    const cur = q.shift()!;
    const d = depth.get(cur)!;
    for (const nb of adj.get(cur) ?? []) {
      if (!depth.has(nb)) {
        depth.set(nb, d + 1);
        q.push(nb);
      }
    }
  }

  const groups: { hubId: string; companyIds: string[] }[] = [];

  // 1. focal company → direct subsidiaries
  const subs = directSubsidiaryIds(edges, focalId);
  if (subs.length > 0) groups.push({ hubId: focalId, companyIds: subs });

  // 2. each person → their deeper company neighbours
  for (const n of nodes) {
    if (n.node_type !== 'person') continue;
    const pd = depth.get(n.id);
    if (pd === undefined) continue;
    const companyIds: string[] = [];
    const seen = new Set<string>();
    for (const nb of adj.get(n.id) ?? []) {
      if (nb === focalId || seen.has(nb)) continue;
      seen.add(nb);
      // Treat every non-person node as a company, matching how the rest of the
      // layout classifies nodes (the backend also emits a "company_entity"
      // type for corporate entities).
      if (typeById.get(nb) === 'person') continue;
      const nd = depth.get(nb);
      if (nd !== undefined && nd > pd) companyIds.push(nb);
    }
    if (companyIds.length > 0) groups.push({ hubId: n.id, companyIds });
  }

  return groups;
}

/**
 * Owners (upstream shareholders) of each company in the graph, with the share
 * percentage from the ownership edge. Owner = source of an upstream-role edge
 * (raw convention: source = owner, target = owned); a known share on any of an
 * owner's edges to that company wins over an unknown one.
 */
function companyOwnerGroups(
  edges: import('@/lib/backend-api').NetworkEdge[],
): { companyId: string; owners: { id: string; share: number | null }[] }[] {
  const byCompany = new Map<string, Map<string, number | null>>();
  for (const e of edges) {
    if (!isUpstreamRole(e.role)) continue;
    const company = e.target;
    const owner = e.source;
    if (owner === company) continue;
    if (!byCompany.has(company)) byCompany.set(company, new Map());
    const m = byCompany.get(company)!;
    const share = e.share_percent ?? null;
    const prev = m.get(owner);
    if (prev === undefined) m.set(owner, share);
    else if (share != null && (prev == null || share > prev))
      m.set(owner, share);
  }
  const groups: {
    companyId: string;
    owners: { id: string; share: number | null }[];
  }[] = [];
  for (const [companyId, m] of byCompany) {
    groups.push({
      companyId,
      owners: [...m.entries()].map(([id, share]) => ({ id, share })),
    });
  }
  return groups;
}

/**
 * Merge company nodes that share the exact same label (company name) into a
 * single node. The backend's multi-level upstream stitching can surface the
 * same company both resolved (id = eu_id) and unresolved (id = canonical_key,
 * node_type "company_entity"), producing visual duplicates. The survivor is the
 * focal, else the node carrying the most identity (eu_id + register info); its
 * missing identity fields are backfilled from the duplicates, all edges are
 * redirected to it, self-loops dropped, and parallel edges de-duplicated.
 * Persons are matched by canonical_key upstream, so only companies merge here.
 */
function mergeSameNameNodes(
  data: CompanyNetworkResponse,
): CompanyNetworkResponse {
  const byLabel = new Map<string, NetworkNode[]>();
  for (const n of data.nodes) {
    if (n.node_type === 'person') continue;
    const key = (n.label ?? '').trim();
    if (!key) continue;
    if (!byLabel.has(key)) byLabel.set(key, []);
    byLabel.get(key)!.push(n);
  }
  const remap = new Map<string, string>();
  const enriched = new Map<string, NetworkNode>();
  const score = (n: NetworkNode) =>
    (n.eu_id ? 2 : 0) + (n.register_court ? 1 : 0);
  for (const [, group] of byLabel) {
    if (group.length < 2) continue;
    const canonical =
      group.find((n) => n.id === data.eu_id) ??
      [...group].sort((a, b) => score(b) - score(a))[0];
    const merged: NetworkNode = { ...canonical };
    for (const n of group) {
      if (n.id === canonical.id) continue;
      remap.set(n.id, canonical.id);
      merged.eu_id = merged.eu_id ?? n.eu_id;
      merged.canonical_key = merged.canonical_key ?? n.canonical_key;
      merged.register_id = merged.register_id ?? n.register_id;
      merged.register_type = merged.register_type ?? n.register_type;
      merged.register_court = merged.register_court ?? n.register_court;
    }
    enriched.set(canonical.id, merged);
  }
  if (remap.size === 0) return data;
  const nodes = data.nodes
    .filter((n) => !remap.has(n.id))
    .map((n) => enriched.get(n.id) ?? n);
  const edgeByKey = new Map<string, import('@/lib/backend-api').NetworkEdge>();
  for (const e of data.edges) {
    const s = remap.get(e.source) ?? e.source;
    const t = remap.get(e.target) ?? e.target;
    if (s === t) continue; // self-loop created by the merge
    const key = `${s}->${t}|${e.role}`;
    const ne = { ...e, source: s, target: t };
    const prev = edgeByKey.get(key);
    if (!prev) edgeByKey.set(key, ne);
    else if (prev.share_percent == null && ne.share_percent != null)
      edgeByKey.set(key, ne);
  }
  return { ...data, nodes, edges: [...edgeByKey.values()] };
}

const NODE_WIDTH = 280;
const NODE_HEIGHT = 44;

const LARGE_GRAPH_THRESHOLD = 10;
/**
 * Filter networkData so that the ELK layered layout produces at most MAX_DEPTH+1 visual rows.
 * Uses directed BFS from the center node (after edge normalisation) to assign each node a
 * signed layer: negative = upstream of center, positive = downstream.
 * Nodes outside the window [-MAX_DEPTH_UP, +MAX_DEPTH_DOWN] are dropped.
 */
/**
 * Filter networkData for the small (non-fullscreen) view to at most 4 ELK visual layers.
 *
 * Strategy:
 * 1. Collect candidates via undirected BFS depth-2 from center (keeps siblings like Solarpark's wind parks).
 * 2. Compute the directed longest-path layer for each candidate.
 * 3. If max directed layer ≤ 3 (≤ 4 ELK rows), use all candidates — no further filtering.
 *    This lets the chain "target → owner company → CEO of that owner" fit in the small view.
 * 4. If max directed layer > 3 (5+ rows), apply a directed depth cap:
 *    2 hops upstream + center + (1 hop down if upstream exists, else 2 hops down).
 */
function filterForSmallView(
  networkData: CompanyNetworkResponse,
  normalisedEdges: import('@/lib/backend-api').NetworkEdge[],
): CompanyNetworkResponse {
  const centerId = networkData.eu_id;
  const allNodes = networkData.nodes;

  // ── Step 1: undirected BFS depth 2 ──────────────────────────────────────
  const unadj = new Map<string, string[]>();
  for (const n of allNodes) unadj.set(n.id, []);
  for (const e of normalisedEdges) {
    unadj.get(e.source)?.push(e.target);
    unadj.get(e.target)?.push(e.source);
  }
  const unDepth = new Map<string, number>([[centerId, 0]]);
  const unQ = [centerId];
  while (unQ.length) {
    const curr = unQ.shift()!;
    const d = unDepth.get(curr)!;
    if (d >= 2) continue;
    for (const nb of unadj.get(curr) ?? []) {
      if (!unDepth.has(nb)) {
        unDepth.set(nb, d + 1);
        unQ.push(nb);
      }
    }
  }
  const candidateIds = new Set(
    allNodes.filter((n) => unDepth.has(n.id)).map((n) => n.id),
  );
  const candidateEdges = normalisedEdges.filter(
    (e) => candidateIds.has(e.source) && candidateIds.has(e.target),
  );

  // ── Step 2: directed longest-path layer per candidate (Kahn's algorithm) ─
  const inDeg = new Map<string, number>();
  const dirAdj = new Map<string, string[]>();
  for (const id of candidateIds) {
    inDeg.set(id, 0);
    dirAdj.set(id, []);
  }
  for (const e of candidateEdges) {
    dirAdj.get(e.source)?.push(e.target);
    inDeg.set(e.target, (inDeg.get(e.target) ?? 0) + 1);
  }
  const dirLayer = new Map<string, number>();
  const topoQ: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) {
      dirLayer.set(id, 0);
      topoQ.push(id);
    }
  }
  while (topoQ.length) {
    const curr = topoQ.shift()!;
    const cl = dirLayer.get(curr)!;
    for (const next of dirAdj.get(curr) ?? []) {
      const nl = cl + 1;
      if (!dirLayer.has(next) || dirLayer.get(next)! < nl)
        dirLayer.set(next, nl);
      inDeg.set(next, inDeg.get(next)! - 1);
      if (inDeg.get(next) === 0) topoQ.push(next);
    }
  }
  const maxLayer = dirLayer.size > 0 ? Math.max(...dirLayer.values()) : 0;

  // ── Step 3: ≤ 4 ELK rows — use all candidates ───────────────────────────
  if (maxLayer <= 3) {
    return {
      ...networkData,
      nodes: allNodes.filter((n) => candidateIds.has(n.id)),
      edges: candidateEdges,
    };
  }

  // ── Step 4: 5+ ELK rows — directed depth cap ─────────────────────────────
  const down = new Map<string, string[]>();
  const up = new Map<string, string[]>();
  for (const id of candidateIds) {
    down.set(id, []);
    up.set(id, []);
  }
  for (const e of candidateEdges) {
    down.get(e.source)?.push(e.target);
    up.get(e.target)?.push(e.source);
  }
  const maxUp = 2;
  const upDepth = new Map<string, number>([[centerId, 0]]);
  const upQ = [centerId];
  while (upQ.length) {
    const curr = upQ.shift()!;
    if (upDepth.get(curr)! >= maxUp) continue;
    for (const nb of up.get(curr) ?? []) {
      if (!upDepth.has(nb)) {
        upDepth.set(nb, upDepth.get(curr)! + 1);
        upQ.push(nb);
      }
    }
  }
  const hasUpstream = upDepth.size > 1;
  const maxDown = hasUpstream ? 1 : 2;
  const downDepth = new Map<string, number>([[centerId, 0]]);
  const downQ = [centerId];
  while (downQ.length) {
    const curr = downQ.shift()!;
    if (downDepth.get(curr)! >= maxDown) continue;
    for (const nb of down.get(curr) ?? []) {
      if (!downDepth.has(nb)) {
        downDepth.set(nb, downDepth.get(curr)! + 1);
        downQ.push(nb);
      }
    }
  }
  const keepIds = new Set<string>();
  for (const id of candidateIds) {
    if (downDepth.has(id) || upDepth.has(id)) keepIds.add(id);
  }
  return {
    ...networkData,
    nodes: allNodes.filter((n) => keepIds.has(n.id)),
    edges: candidateEdges.filter(
      (e) => keepIds.has(e.source) && keepIds.has(e.target),
    ),
  };
}

// ---------------------------------------------------------------------------
const MAX_PER_LAYER = 5;
const MAX_DISPLAY_NODES = 10;

/**
 * Map each owner node to the highest ownership stake (share_percent) it holds
 * via any upstream/ownership edge (Gesellschafter, Aktionär, Kommanditist, …).
 * After normaliseEdges the owner is always the edge source. Only edges that
 * actually carry a percentage are included, so the map holds exactly the owners
 * whose stake is known — CEOs/managers and unknown-stake owners are absent.
 * Used by the overview to keep the largest shareholders when a layer is capped.
 */
function ownershipShareByNode(
  edges: import('@/lib/backend-api').NetworkEdge[],
): Map<string, number> {
  const share = new Map<string, number>();
  for (const e of edges) {
    if (e.share_percent == null) continue;
    if (!isUpstreamRole(e.role)) continue;
    const prev = share.get(e.source);
    if (prev === undefined || e.share_percent > prev) {
      share.set(e.source, e.share_percent);
    }
  }
  return share;
}

/**
 * Reorder ONLY the known-stake owners within an already-sorted id list so that
 * owners holding a larger stake come first, while every other node (CEOs,
 * managers, unknown-stake owners) keeps its exact position. A count cap that
 * slices the front of the list then retains the largest shareholders first,
 * without disturbing the surrounding ordering. Owners are permuted only among
 * the slots they already occupy, so transitivity is never an issue.
 */
function prioritiseOwnersByShare(
  sortedIds: string[],
  share: Map<string, number>,
): string[] {
  const positions: number[] = [];
  const owners: string[] = [];
  sortedIds.forEach((id, i) => {
    if (share.has(id)) {
      positions.push(i);
      owners.push(id);
    }
  });
  if (owners.length < 2) return sortedIds;
  const reordered = owners
    .map((id, idx) => ({ id, idx, pct: share.get(id)! }))
    // Higher stake first; ties keep their prior relative order (stable).
    .sort((a, b) => b.pct - a.pct || a.idx - b.idx)
    .map((o) => o.id);
  const out = sortedIds.slice();
  positions.forEach((pos, k) => {
    out[pos] = reordered[k];
  });
  return out;
}

/** Drop excess nodes per directed layer, always keeping the center node. */
function trimLayers(
  centerId: string,
  nodes: import('@/lib/backend-api').NetworkNode[],
  edges: import('@/lib/backend-api').NetworkEdge[],
): {
  nodes: import('@/lib/backend-api').NetworkNode[];
  edges: import('@/lib/backend-api').NetworkEdge[];
} {
  // Directed layer assignment via Kahn's longest-path
  const ids = new Set(nodes.map((n) => n.id));
  const inDeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of edges) {
    if (!ids.has(e.source) || !ids.has(e.target)) continue;
    adj.get(e.source)!.push(e.target);
    inDeg.set(e.target, (inDeg.get(e.target) ?? 0) + 1);
  }
  const layer = new Map<string, number>();
  const q: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) {
      layer.set(id, 0);
      q.push(id);
    }
  }
  while (q.length) {
    const cur = q.shift()!;
    for (const nxt of adj.get(cur) ?? []) {
      const nl = (layer.get(cur) ?? 0) + 1;
      if (!layer.has(nxt) || layer.get(nxt)! < nl) layer.set(nxt, nl);
      inDeg.set(nxt, inDeg.get(nxt)! - 1);
      if (inDeg.get(nxt) === 0) q.push(nxt);
    }
  }

  // Undirected BFS distance from center
  const unadj = new Map<string, string[]>();
  for (const n of nodes) unadj.set(n.id, []);
  for (const e of edges) {
    unadj.get(e.source)?.push(e.target);
    unadj.get(e.target)?.push(e.source);
  }
  const bfsDist = new Map<string, number>([[centerId, 0]]);
  const bfsQ = [centerId];
  while (bfsQ.length) {
    const cur = bfsQ.shift()!;
    const d = bfsDist.get(cur)!;
    for (const nb of unadj.get(cur) ?? []) {
      if (!bfsDist.has(nb)) {
        bfsDist.set(nb, d + 1);
        bfsQ.push(nb);
      }
    }
  }

  // Count degree per node
  const degree = new Map<string, number>();
  for (const n of nodes) degree.set(n.id, 0);
  for (const e of edges) {
    if (ids.has(e.source))
      degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    if (ids.has(e.target))
      degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  }

  // Among multiple owners competing for a capped layer slot, the ones holding a
  // larger stake should survive first. CEOs / managers are unaffected.
  const ownerShare = ownershipShareByNode(edges);

  // Group by layer, trim to MAX_PER_LAYER: center first, then closer nodes, then higher degree
  const byLayer = new Map<number, string[]>();
  for (const [id, l] of layer) {
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l)!.push(id);
  }
  const keepIds = new Set<string>();
  for (const [, layerNodes] of byLayer) {
    const sorted = layerNodes.sort((a, b) => {
      if (a === centerId) return -1;
      if (b === centerId) return 1;
      const distDiff = (bfsDist.get(a) ?? 99) - (bfsDist.get(b) ?? 99);
      if (distDiff !== 0) return distDiff;
      return (degree.get(b) ?? 0) - (degree.get(a) ?? 0);
    });
    const ordered = prioritiseOwnersByShare(sorted, ownerShare);
    ordered.slice(0, MAX_PER_LAYER).forEach((id) => keepIds.add(id));
  }

  // Chain preservation: when we keep a non-centre company that sits above
  // the focal (e.g. a Gesellschafter / parent), also keep one of its person
  // predecessors (a CEO / manager of that company) if any exist. This is
  // what makes the "owner company → CEO of that owner" chain reach four
  // visual layers in the small view.
  const centerLayer = layer.get(centerId) ?? 0;
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const incoming = new Map<string, string[]>();
  for (const id of ids) incoming.set(id, []);
  for (const e of edges) {
    if (ids.has(e.source) && ids.has(e.target)) {
      incoming.get(e.target)!.push(e.source);
    }
  }
  for (const id of [...keepIds]) {
    if (id === centerId) continue;
    if (nodeMap.get(id)?.node_type !== 'company') continue;
    const idLayer = layer.get(id);
    if (idLayer === undefined || idLayer >= centerLayer) continue;
    const personPreds = (incoming.get(id) ?? []).filter(
      (p) => nodeMap.get(p)?.node_type === 'person',
    );
    if (personPreds.length === 0) continue;
    if (personPreds.some((p) => keepIds.has(p))) continue;
    const best = [...personPreds].sort(
      (a, b) => (degree.get(b) ?? 0) - (degree.get(a) ?? 0),
    )[0];
    keepIds.add(best);
  }

  return {
    nodes: nodes.filter((n) => keepIds.has(n.id)),
    edges: edges.filter((e) => keepIds.has(e.source) && keepIds.has(e.target)),
  };
}

async function getElkLayout(
  networkData: CompanyNetworkResponse,
  applyDepthFilter = true,
  isMobile = false,
  disableNormalization = false,
): Promise<{ nodes: Node[]; edges: Edge[]; roleTypes: string[] }> {
  // Normalise first so the directed depth filter uses correct edge directions.
  // Callers can opt out (e.g. the homepage demo) when they've already oriented
  // the edges themselves and want ELK to honour that orientation directly.
  const nodeMapAll = new Map(networkData.nodes.map((n) => [n.id, n]));
  const normalisedAll = disableNormalization
    ? networkData.edges
    : normaliseEdges(networkData.edges, nodeMapAll);
  const depthFiltered = applyDepthFilter
    ? filterForSmallView(networkData, normalisedAll)
    : { ...networkData, edges: normalisedAll };
  let filtered = applyDepthFilter
    ? {
        ...depthFiltered,
        ...trimLayers(
          networkData.eu_id,
          depthFiltered.nodes,
          depthFiltered.edges,
        ),
      }
    : depthFiltered;

  // Cap total displayed nodes to MAX_DISPLAY_NODES, prioritising closeness then degree
  if (applyDepthFilter && filtered.nodes.length > MAX_DISPLAY_NODES) {
    const capCenterId = networkData.eu_id;
    const capUnadj = new Map<string, string[]>();
    for (const n of filtered.nodes) capUnadj.set(n.id, []);
    for (const e of filtered.edges) {
      capUnadj.get(e.source)?.push(e.target);
      capUnadj.get(e.target)?.push(e.source);
    }
    const capDist = new Map<string, number>([[capCenterId, 0]]);
    const capQ = [capCenterId];
    while (capQ.length) {
      const cur = capQ.shift()!;
      const d = capDist.get(cur)!;
      for (const nb of capUnadj.get(cur) ?? []) {
        if (!capDist.has(nb)) {
          capDist.set(nb, d + 1);
          capQ.push(nb);
        }
      }
    }
    const capDegree = new Map<string, number>();
    for (const n of filtered.nodes) capDegree.set(n.id, 0);
    for (const e of filtered.edges) {
      capDegree.set(e.source, (capDegree.get(e.source) ?? 0) + 1);
      capDegree.set(e.target, (capDegree.get(e.target) ?? 0) + 1);
    }
    // Directed-layer side of each node relative to the center, so upstream
    // (parents / Gesellschafter) survive the cap when they tie on undirected
    // distance with high-degree downstream subsidiaries.
    const capInDeg = new Map<string, number>();
    const capAdj = new Map<string, string[]>();
    for (const n of filtered.nodes) {
      capInDeg.set(n.id, 0);
      capAdj.set(n.id, []);
    }
    for (const e of filtered.edges) {
      capAdj.get(e.source)?.push(e.target);
      capInDeg.set(e.target, (capInDeg.get(e.target) ?? 0) + 1);
    }
    const capLayer = new Map<string, number>();
    const capLayerQ: string[] = [];
    const capInDegCopy = new Map(capInDeg);
    for (const [id, deg] of capInDegCopy) {
      if (deg === 0) {
        capLayer.set(id, 0);
        capLayerQ.push(id);
      }
    }
    while (capLayerQ.length) {
      const cur = capLayerQ.shift()!;
      for (const nxt of capAdj.get(cur) ?? []) {
        const nl = (capLayer.get(cur) ?? 0) + 1;
        if (!capLayer.has(nxt) || capLayer.get(nxt)! < nl)
          capLayer.set(nxt, nl);
        capInDegCopy.set(nxt, capInDegCopy.get(nxt)! - 1);
        if (capInDegCopy.get(nxt) === 0) capLayerQ.push(nxt);
      }
    }
    const capCenterLayer = capLayer.get(capCenterId) ?? 0;
    const sideRank = (id: string) => {
      const l = capLayer.get(id);
      if (l === undefined) return 2;
      if (l < capCenterLayer) return 0; // upstream (parents / Gesellschafter)
      if (l === capCenterLayer) return 1;
      return 2; // downstream (subsidiaries)
    };
    const sorted = [...filtered.nodes].sort((a, b) => {
      if (a.id === capCenterId) return -1;
      if (b.id === capCenterId) return 1;
      const distDiff = (capDist.get(a.id) ?? 99) - (capDist.get(b.id) ?? 99);
      if (distDiff !== 0) return distDiff;
      const sideDiff = sideRank(a.id) - sideRank(b.id);
      if (sideDiff !== 0) return sideDiff;
      return (capDegree.get(b.id) ?? 0) - (capDegree.get(a.id) ?? 0);
    });
    const capOwnerShare = ownershipShareByNode(filtered.edges);
    const orderedIds = prioritiseOwnersByShare(
      sorted.map((n) => n.id),
      capOwnerShare,
    );
    const capIds = new Set(orderedIds.slice(0, MAX_DISPLAY_NODES));
    filtered = {
      ...filtered,
      nodes: filtered.nodes.filter((n) => capIds.has(n.id)),
      edges: filtered.edges.filter(
        (e) => capIds.has(e.source) && capIds.has(e.target),
      ),
    };
  }

  // ── Top row cap: ELK's top visual row = all in-degree-0 nodes in the visible
  // graph. trimLayers caps per directed layer, but nodes whose directed parents
  // were dropped (e.g. Matthias after HRB16240FL is cut) also float to row 0.
  // Cap the actual in-degree-0 set to MAX_PER_LAYER before ELK sees them. ──
  if (applyDepthFilter) {
    const topInDeg = new Map<string, number>();
    for (const n of filtered.nodes) topInDeg.set(n.id, 0);
    for (const e of filtered.edges)
      topInDeg.set(e.target, (topInDeg.get(e.target) ?? 0) + 1);
    const topRow = filtered.nodes.filter(
      (n) => (topInDeg.get(n.id) ?? 0) === 0,
    );
    if (topRow.length > MAX_PER_LAYER) {
      const topDeg = new Map<string, number>();
      for (const n of filtered.nodes) topDeg.set(n.id, 0);
      for (const e of filtered.edges) {
        topDeg.set(e.source, (topDeg.get(e.source) ?? 0) + 1);
        topDeg.set(e.target, (topDeg.get(e.target) ?? 0) + 1);
      }
      // Chain-support bonus: a top-row node that points to a kept non-centre
      // node is acting as the "CEO of the owner". Prefer those over plain
      // CEOs that only point at the focal, so the upper chain stays visible.
      const topCenterId = filtered.eu_id;
      const topConnector = new Map<string, number>();
      for (const n of filtered.nodes) topConnector.set(n.id, 0);
      for (const e of filtered.edges) {
        if (e.target !== topCenterId) {
          topConnector.set(e.source, (topConnector.get(e.source) ?? 0) + 1);
        }
      }
      const topOwnerShare = ownershipShareByNode(filtered.edges);
      const sortedTop = [...topRow]
        .sort((a, b) => {
          const ca = topConnector.get(a.id) ?? 0;
          const cb = topConnector.get(b.id) ?? 0;
          if (ca !== cb) return cb - ca;
          return (topDeg.get(b.id) ?? 0) - (topDeg.get(a.id) ?? 0);
        })
        .map((n) => n.id);
      const keepTop = new Set(
        prioritiseOwnersByShare(sortedTop, topOwnerShare).slice(
          0,
          MAX_PER_LAYER,
        ),
      );
      const dropTop = new Set(
        topRow.filter((n) => !keepTop.has(n.id)).map((n) => n.id),
      );
      filtered = {
        ...filtered,
        nodes: filtered.nodes.filter((n) => !dropTop.has(n.id)),
        edges: filtered.edges.filter(
          (e) => !dropTop.has(e.source) && !dropTop.has(e.target),
        ),
      };
    }
  }

  // ── Sibling row cap: trimLayers works on directed layers, but ELK groups all
  // nodes reachable from upOfCenter (that aren't downstream of center) onto the
  // same visual row. Cap that set to MAX_PER_LAYER now, before ELK sees them. ──
  if (applyDepthFilter) {
    const sibCenter = filtered.eu_id;
    const sibUp = new Set<string>();
    for (const e of filtered.edges) {
      if (e.target === sibCenter) sibUp.add(e.source);
    }
    const sibDown = new Set<string>();
    for (const e of filtered.edges) {
      if (e.source === sibCenter) sibDown.add(e.target);
    }
    if (sibDown.size === 0) {
      const sibSet = new Set<string>();
      for (const e of filtered.edges) {
        if (sibUp.has(e.source) && e.target !== sibCenter) sibSet.add(e.target);
      }
      if (sibSet.size > MAX_PER_LAYER) {
        const sibDeg = new Map<string, number>();
        for (const n of filtered.nodes) sibDeg.set(n.id, 0);
        for (const e of filtered.edges) {
          sibDeg.set(e.source, (sibDeg.get(e.source) ?? 0) + 1);
          sibDeg.set(e.target, (sibDeg.get(e.target) ?? 0) + 1);
        }
        const keepSibs = new Set(
          [...sibSet]
            .sort((a, b) => (sibDeg.get(b) ?? 0) - (sibDeg.get(a) ?? 0))
            .slice(0, MAX_PER_LAYER),
        );
        const dropSibs = new Set([...sibSet].filter((id) => !keepSibs.has(id)));
        filtered = {
          ...filtered,
          nodes: filtered.nodes.filter((n) => !dropSibs.has(n.id)),
          edges: filtered.edges.filter(
            (e) => !dropSibs.has(e.source) && !dropSibs.has(e.target),
          ),
        };
      }
    }
  }

  const visibleIds = new Set(filtered.nodes.map((n) => n.id));
  const nodeMap = new Map(filtered.nodes.map((n) => [n.id, n]));
  const normalisedEdges = filtered.edges; // already normalised + filtered

  // When the same (source, target) pair has both an upstream role
  // (Gesellschafter / Aktionär / …) and a downstream role (Geschäftsführer /
  // Vorstand / …) we collapse them into a single edge. The combined label
  // lists the ownership stake alongside the management role, and the renderer
  // applies a dot-dashed "third color" style so the merged relationship reads
  // as its own category rather than a Gesellschafter line on top of a
  // Geschäftsführer line.
  type MergedEdge = import('@/lib/backend-api').NetworkEdge & {
    mergedRole?: string;
    isMerged?: boolean;
  };
  const visibleEdges = normalisedEdges.filter(
    (e) => visibleIds.has(e.source) && visibleIds.has(e.target),
  );
  const mergedEdgeList: MergedEdge[] = (() => {
    const pairToEdges = new Map<string, typeof visibleEdges>();
    for (const e of visibleEdges) {
      const key = `${e.source}->${e.target}`;
      const arr = pairToEdges.get(key) ?? [];
      arr.push(e);
      pairToEdges.set(key, arr);
    }
    const out: MergedEdge[] = [];
    const seen = new Set<string>();
    for (const e of visibleEdges) {
      const key = `${e.source}->${e.target}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const arr = pairToEdges.get(key)!;
      const upstream = arr.find((x) => isUpstreamRole(x.role));
      const downstream = arr.find((x) => isManagingRole(x.role));
      if (arr.length > 1 && upstream && downstream) {
        out.push({ ...upstream, mergedRole: downstream.role, isMerged: true });
      } else {
        for (const x of arr) out.push(x);
      }
    }
    return out;
  })();

  // Per-node size based on label text. Computed once so the same values are
  // fed to both ELK (for layout) and the React Flow node data (for rendering).
  const sizeByNodeId = new Map<string, { width: number; height: number }>();
  for (const n of filtered.nodes) {
    const nt: 'center' | 'company' | 'person' =
      n.id === filtered.eu_id
        ? 'center'
        : n.node_type === 'person'
          ? 'person'
          : 'company';
    const hasExt = nt === 'company' && !!n.eu_id && !!n.register_court;
    sizeByNodeId.set(n.id, computeNodeSize(n.label, nt, hasExt));
  }
  freezeBreadcrumb('elk-sync-sizes-done');

  // ── Visual row chunking: ELK normally puts every node in a directed layer
  // onto a single visual row, which produces a 100-node-wide ribbon for
  // companies with many Geschäftsführer or many Tochter. We assign each node a
  // longest-path layer, and any layer with more than MAX_VISUAL_NODES_PER_ROW
  // nodes is split into chunks via per-node `elk.partitioning.partition`. The
  // chunks above the focal stay above (their partition < focal's), and the
  // chunks below the focal stay below.
  const MAX_VISUAL_NODES_PER_ROW = 8;
  const partitionOf = new Map<string, number>();
  {
    // Two-pass layering. Pass 1 lays out the COMPANY hierarchy only (ownership +
    // subsidiary edges); managers are excluded so they don't inflate it. Pass 2
    // places each person one row above the DEEPEST company it points to — so a
    // daughter company's CEO sits with the daughter (below the focal) instead of
    // floating to the top of the graph as an in-degree-0 source.
    //
    // Strict ownership: every Gesellschafter / Kommanditist (and other upstream
    // owner) must sit ABOVE the company it holds. Longest-path layering gives
    // that (each edge drops one row). Plain longest-path stalls on ownership
    // cycles (GmbH & Co. KG, where a subsidiary is also the Komplementär), so we
    // first break cycles with a DFS, exploring ownership edges before the rest so
    // the dropped back-edge is preferentially the non-ownership one.
    const isPersonNode = (id: string) =>
      nodeMap.get(id)?.node_type === 'person';
    const companyIds = filtered.nodes
      .filter((n) => !isPersonNode(n.id))
      .map((n) => n.id);
    const adjList = new Map<string, string[]>();
    for (const id of companyIds) adjList.set(id, []);
    const ownerFirst: [string, string][] = [];
    const rest: [string, string][] = [];
    for (const e of normalisedEdges) {
      if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) continue;
      if (isPersonNode(e.source) || isPersonNode(e.target)) continue;
      (isUpstreamRole(e.role) ? ownerFirst : rest).push([e.source, e.target]);
    }
    for (const [s, t] of ownerFirst) adjList.get(s)!.push(t);
    for (const [s, t] of rest) adjList.get(s)!.push(t);

    // Iterative DFS; an edge to a node currently on the stack (GRAY) is a
    // back-edge and gets dropped to break its cycle.
    const WHITE = 0,
      GRAY = 1,
      BLACK = 2;
    const color = new Map<string, number>();
    for (const id of companyIds) color.set(id, WHITE);
    const backEdges = new Set<string>();
    const ekey = (s: string, t: string) => `${s}${t}`;
    for (const startId of companyIds) {
      if (color.get(startId) !== WHITE) continue;
      const stack: { id: string; i: number }[] = [{ id: startId, i: 0 }];
      color.set(startId, GRAY);
      while (stack.length) {
        const top = stack[stack.length - 1];
        const nbrs = adjList.get(top.id)!;
        if (top.i < nbrs.length) {
          const nxt = nbrs[top.i++];
          const c = color.get(nxt);
          if (c === WHITE) {
            color.set(nxt, GRAY);
            stack.push({ id: nxt, i: 0 });
          } else if (c === GRAY) {
            backEdges.add(ekey(top.id, nxt));
          }
        } else {
          color.set(top.id, BLACK);
          stack.pop();
        }
      }
    }

    // Longest-path layering (Kahn) over the cycle-broken DAG: every kept edge
    // goes strictly downward, so owners always land above what they own.
    const inDeg = new Map<string, number>();
    const dag = new Map<string, string[]>();
    for (const id of companyIds) {
      inDeg.set(id, 0);
      dag.set(id, []);
    }
    for (const [s, nbrs] of adjList) {
      for (const t of nbrs) {
        if (backEdges.has(ekey(s, t))) continue;
        dag.get(s)!.push(t);
        inDeg.set(t, (inDeg.get(t) ?? 0) + 1);
      }
    }
    const ldLayer = new Map<string, number>();
    const ldQ: string[] = [];
    for (const [id, deg] of inDeg) {
      if (deg === 0) {
        ldLayer.set(id, 0);
        ldQ.push(id);
      }
    }
    while (ldQ.length) {
      const cur = ldQ.shift()!;
      const cl = ldLayer.get(cur) ?? 0;
      for (const nxt of dag.get(cur) ?? []) {
        if ((ldLayer.get(nxt) ?? -Infinity) < cl + 1) ldLayer.set(nxt, cl + 1);
        inDeg.set(nxt, inDeg.get(nxt)! - 1);
        if (inDeg.get(nxt) === 0) ldQ.push(nxt);
      }
    }
    for (const id of companyIds) {
      if (!ldLayer.has(id)) ldLayer.set(id, 0);
    }
    // Double the company layers so each company has a spare row above it (its
    // parent level is 2 rows up, not 1). That spare odd row holds the company's
    // managers, so a daughter's CEO sits between the daughter and the focal —
    // above the daughter, below the focal.
    for (const id of companyIds) {
      ldLayer.set(id, (ldLayer.get(id) ?? 0) * 2);
    }

    const focalLayer = ldLayer.get(filtered.eu_id) ?? 0;

    // Pass 1.5: companies connected only through people (no company-graph edge),
    // e.g. a shared CEO's other firm, are stranded at the top by pass 1. Anchor
    // each one one company level below the focal so it sits among the lower
    // companies.
    const companyHasEdge = new Set<string>();
    for (const [s, nbrs] of adjList) {
      if (nbrs.length > 0) companyHasEdge.add(s);
      for (const t of nbrs) companyHasEdge.add(t);
    }
    for (const id of companyIds) {
      if (!companyHasEdge.has(id)) ldLayer.set(id, focalLayer + 2);
    }

    // Pass 2: place each person one row above the SHALLOWEST company it points
    // to. If it is tied to the TARGET or a parent it sits above that (the target
    // takes priority — a person who is CEO of both the target and a daughter is
    // shown above the target). A person tied only to companies below the focal
    // (a pure daughter's CEO) sits below the focal but above those daughters.
    const personLayers = new Map<string, number[]>();
    for (const e of normalisedEdges) {
      if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) continue;
      if (!isPersonNode(e.source) || isPersonNode(e.target)) continue;
      const tl = ldLayer.get(e.target);
      if (tl === undefined) continue;
      if (!personLayers.has(e.source)) personLayers.set(e.source, []);
      personLayers.get(e.source)!.push(tl);
    }
    for (const n of filtered.nodes) {
      if (!isPersonNode(n.id)) continue;
      const layers = personLayers.get(n.id);
      if (!layers || layers.length === 0) {
        ldLayer.set(n.id, focalLayer - 1);
        continue;
      }
      const atOrAbove = layers.filter((l) => l <= focalLayer);
      const below = layers.filter((l) => l > focalLayer);
      if (atOrAbove.length > 0) {
        // tied to the target or a parent → above it (target takes priority)
        ldLayer.set(n.id, Math.min(...atOrAbove) - 1);
      } else if (below.length > 0) {
        // a pure daughter's CEO → between the focal and its shallowest daughter
        ldLayer.set(n.id, Math.min(...below) - 1);
      } else {
        ldLayer.set(n.id, focalLayer - 1);
      }
    }
    const minLayer = Math.min(0, ...ldLayer.values());

    const byLdLayer = new Map<number, string[]>();
    for (const [id, l] of ldLayer) {
      if (!byLdLayer.has(l)) byLdLayer.set(l, []);
      byLdLayer.get(l)!.push(id);
    }
    // Each layer gets a partition base of `(l - minLayer) * 1000` (non-negative,
    // order preserved), leaving room for up to 1000 chunk indices per layer.
    // Chunks within an oversized layer get base + chunkIndex, so they appear as
    // separate visual rows ordered downward — and stay between adjacent layers.
    for (const [l, layerNodes] of byLdLayer) {
      const base = (l - minLayer) * 1000;
      if (layerNodes.length <= MAX_VISUAL_NODES_PER_ROW) {
        for (const id of layerNodes) partitionOf.set(id, base);
      } else {
        // Sort deterministically so the chunking is stable across renders.
        const sorted = [...layerNodes].sort();
        sorted.forEach((id, idx) => {
          partitionOf.set(
            id,
            base + Math.floor(idx / MAX_VISUAL_NODES_PER_ROW),
          );
        });
      }
    }
  }
  const hasChunkedLayer = [...partitionOf.values()].some((p) => p % 1000 !== 0);
  freezeBreadcrumb('elk-sync-partition-done', { hasChunkedLayer });

  const elkGraph = {
    id: 'root',
    layoutOptions: {
      algorithm: 'org.eclipse.elk.layered',
      'elk.direction': isMobile ? 'RIGHT' : 'DOWN',
      ...(isMobile
        ? {
            // Mobile: left-to-right flow, uses vertical space
            'elk.layered.wrapping.strategy': 'NONE',
            'elk.aspectRatio': '0.4',
            'elk.spacing.nodeNode': '12',
            'elk.layered.spacing.nodeNodeBetweenLayers': '30',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '10',
            'elk.layered.spacing.edgeNodeBetweenLayers': '10',
          }
        : {
            // Desktop: allow wrapping for wider layout
            'elk.layered.wrapping.strategy': hasChunkedLayer
              ? 'OFF'
              : 'MULTI_EDGE',
            'elk.layered.wrapping.multiEdge.improvementIterations': '10',
            'elk.aspectRatio': '1.2',
            'elk.spacing.nodeNode': '20',
            'elk.layered.spacing.nodeNodeBetweenLayers': '40',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '15',
            'elk.layered.spacing.edgeNodeBetweenLayers': '15',
          }),
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      // Valid GreedySwitchType constants: OFF | ONE_SIDED | TWO_SIDED.
      // 'TWO_SIDED_GREEDY_SWITCH' was invalid and silently ignored by ELK
      // (internally thrown + caught per layout — debugger noise).
      'elk.layered.crossingMinimization.greedySwitch.type': 'TWO_SIDED',
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.padding': '[top=10,left=10,bottom=10,right=10]',
      // Activate partitioning only when at least one layer needs chunking.
      // The per-node `elk.partitioning.partition` values then drive ELK's
      // layer assignment instead of the natural edge-based layering.
      ...(hasChunkedLayer ? { 'elk.partitioning.activate': 'true' } : {}),
    },
    children: filtered.nodes.map((n) => {
      const sz = sizeByNodeId.get(n.id);
      return {
        id: n.id,
        width: sz?.width ?? NODE_WIDTH,
        height: sz?.height ?? NODE_HEIGHT,
        ...(hasChunkedLayer
          ? {
              layoutOptions: {
                'elk.partitioning.partition': String(
                  partitionOf.get(n.id) ?? 0,
                ),
              },
            }
          : {}),
      };
    }),
    edges: mergedEdgeList.map((e, i) => ({
      id: `edge-${i}`,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  // We previously injected artificial centre→sibling edges to force a third
  // ELK row. Under the current centre-aware orientation every related entity
  // is already a source pointing AT the centre, so those artificial edges
  // would form cycles with the real ownership edges and ELK would resolve
  // them by reversing the real edge — pushing Gesellschafter companies below
  // the focal. The natural hierarchy now produces all the layers we need.

  // Sync prep is done past this crumb — a trail ending here means the freeze
  // is in the worker hand-off or whatever ran after this task yielded.
  freezeBreadcrumb('network-elk-post', {
    children: elkGraph.children.length,
    edges: elkGraph.edges.length,
  });
  const elkT0 = performance.now();
  const layout = await elkLayout(elkGraph);
  // Separates elk time from the JS post-processing below; `mode` says whether
  // the worker or the main-thread fallback did the work.
  freezeBreadcrumb('network-elk-done', {
    elkMs: Math.round(performance.now() - elkT0),
    mode: elkLastMode(),
  });

  // Build a map from ELK edge ID → routed SVG path + points
  const elkEdgeData = new Map<
    string,
    { path: string; points: { x: number; y: number }[] }
  >();
  for (const elkEdge of (layout.edges ??
    []) as import('elkjs/lib/elk-api').ElkExtendedEdge[]) {
    if (!elkEdge.id || elkEdge.id.startsWith('art-')) continue;
    const section = elkEdge.sections?.[0];
    if (!section) continue;
    const pts: { x: number; y: number }[] = [
      section.startPoint,
      ...(section.bendPoints ?? []),
      section.endPoint,
    ];
    const d = pts
      .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ');
    elkEdgeData.set(elkEdge.id, { path: d, points: pts });
  }

  // Build node position lookup for fallback path computation
  const nodePos = new Map<
    string,
    { x: number; y: number; w: number; h: number }
  >();
  for (const child of layout.children ?? []) {
    nodePos.set(child.id, {
      x: child.x ?? 0,
      y: child.y ?? 0,
      w: child.width ?? NODE_WIDTH,
      h: child.height ?? NODE_HEIGHT,
    });
  }

  const finalEdgeList = mergedEdgeList;

  // Set handle positions based on flow direction
  const sourcePos = isMobile ? Position.Right : Position.Bottom;
  const targetPos = isMobile ? Position.Left : Position.Top;

  const nodes: Node[] = (layout.children ?? []).map((child) => {
    const src = filtered.nodes.find((n) => n.id === child.id)!;
    const nodeType =
      child.id === filtered.eu_id
        ? 'center'
        : src.node_type === 'person'
          ? 'person'
          : 'company';
    return {
      id: child.id,
      type: nodeType,
      position: { x: child.x ?? 0, y: child.y ?? 0 },
      sourcePosition: sourcePos,
      targetPosition: targetPos,
      data: {
        label: src.label,
        nodeType,
        eu_id: src.eu_id ?? null,
        canonical_key: src.canonical_key ?? null,
        register_id: src.register_id ?? null,
        register_type: src.register_type ?? null,
        register_court: src.register_court ?? null,
        width: sizeByNodeId.get(child.id)?.width ?? NODE_WIDTH,
        height: sizeByNodeId.get(child.id)?.height ?? NODE_HEIGHT,
      },
      style: { background: 'transparent', border: 'none', padding: 0 },
    };
  });

  const edges: Edge[] = finalEdgeList.map((e, i) => {
    const elk = elkEdgeData.get(`edge-${i}`);
    let path = elk?.path;
    let points = elk?.points;
    // Fallback: compute a simple orthogonal path from node edges
    if (!path) {
      const src = nodePos.get(e.source);
      const tgt = nodePos.get(e.target);
      if (src && tgt) {
        if (isMobile) {
          // Horizontal: source right edge → target left edge
          const sx = src.x + src.w;
          const sy = src.y + src.h / 2;
          const tx = tgt.x;
          const ty = tgt.y + tgt.h / 2;
          const midX = (sx + tx) / 2;
          path = `M ${sx} ${sy} L ${midX} ${sy} L ${midX} ${ty} L ${tx} ${ty}`;
          points = [
            { x: sx, y: sy },
            { x: midX, y: sy },
            { x: midX, y: ty },
            { x: tx, y: ty },
          ];
        } else {
          // Vertical: source bottom edge → target top edge
          const sx = src.x + src.w / 2;
          const sy = src.y + src.h;
          const tx = tgt.x + tgt.w / 2;
          const ty = tgt.y;
          const midY = (sy + ty) / 2;
          path = `M ${sx} ${sy} L ${sx} ${midY} L ${tx} ${midY} L ${tx} ${ty}`;
          points = [
            { x: sx, y: sy },
            { x: sx, y: midY },
            { x: tx, y: midY },
            { x: tx, y: ty },
          ];
        }
      }
    }
    const sharePart =
      e.share_percent != null ? formatSharePercent(e.share_percent) : null;
    const label = e.isMerged
      ? sharePart
        ? `${e.role} ${sharePart} und ${e.mergedRole}`
        : `${e.role} und ${e.mergedRole}`
      : sharePart
        ? `${e.role} · ${sharePart}`
        : e.role;
    return {
      id: `edge-${i}`,
      type: 'orthogonal' as const,
      source: e.source,
      target: e.target,
      label,
      data: {
        role: e.role,
        mergedRole: e.mergedRole,
        isMerged: !!e.isMerged,
        elkPath: path ?? '',
        elkPoints: points ?? [],
      },
      style: { stroke: '#d6d3c8' },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        color: '#d6d3c8',
      },
    };
  });

  // Collect distinct role types present in the final edge set
  const roleTypes = Array.from(
    new Set(finalEdgeList.map((e) => e.role).filter(Boolean)),
  ).sort();

  return { nodes, edges, roleTypes };
}

// ---------------------------------------------------------------------------
// Company detail panel
// ---------------------------------------------------------------------------
function CompanyDetailPanel({
  euId,
  label,
  onClose,
  onNavigate,
  floating = true,
}: {
  euId: string;
  label: string;
  onClose: () => void;
  onNavigate?: () => void;
  floating?: boolean;
}) {
  const [company, setCompany] = useState<CompanyBasicResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/companies/${encodeURIComponent(euId)}/unified/basic`)
      .then((r) => {
        if (!r.ok) return null;
        return r.json();
      })
      .then((json) => setCompany(json?.data ?? json ?? null))
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [euId]);

  const latestMetric = company?.metrics?.sort((a, b) => b.year - a.year)[0];

  const fmt = (n?: number | null) =>
    n != null
      ? new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(n)
      : null;

  return (
    <div
      className={`${floating ? 'absolute right-3 top-3 z-10' : 'w-full'} w-72 rounded-xl border border-border bg-card shadow-lg`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 overflow-hidden">
            {getPublicStorageUrl(company?.favicon_url) ||
            getPublicStorageUrl(company?.logo_url) ? (
              <img
                src={
                  getPublicStorageUrl(company?.logo_url) ??
                  getPublicStorageUrl(company?.favicon_url)
                }
                alt={`${company?.display_name ?? ''} Logo`}
                className="h-5 w-5 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <Building2 size={14} />
            )}
          </div>
          <span className="text-sm font-semibold line-clamp-1">
            {loading ? '…' : (company?.display_name ?? label)}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-4 py-3 space-y-2">
        {loading && (
          <p className="text-xs text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        )}
        {!loading && company && (
          <>
            {company.sitz && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  {t('company.city', 'Sitz')}
                </span>
                <span className="font-medium">{company.sitz}</span>
              </div>
            )}
            {company.register_type && company.register_id && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  {t('company.register', 'Register')}
                </span>
                <span className="font-medium">
                  {company.register_type} {company.register_id}
                </span>
              </div>
            )}
            {latestMetric && (
              <>
                {latestMetric.total_revenue != null && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {t('company.revenue', 'Umsatz')} {latestMetric.year}
                    </span>
                    <span className="font-medium">
                      {fmt(latestMetric.total_revenue)}
                    </span>
                  </div>
                )}
                {latestMetric.profit != null && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {t('company.profit', 'Gewinn')} {latestMetric.year}
                    </span>
                    <span
                      className={`font-medium ${latestMetric.profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}
                    >
                      {fmt(latestMetric.profit)}
                    </span>
                  </div>
                )}
              </>
            )}
            {company.employee_data?.[0] && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  {t('company.employees', 'Mitarbeiter')}
                </span>
                <span className="font-medium">
                  {company.employee_data[0].total?.toLocaleString('de-DE')}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {onNavigate && (
        <div className="border-t border-border px-4 py-3">
          <button
            onClick={onNavigate}
            className="w-full rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('network.viewCompany', 'Zum Unternehmen →')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Person detail panel
// ---------------------------------------------------------------------------
function PersonDetailPanel({
  canonicalKey,
  onClose,
  floating = true,
}: {
  canonicalKey: string;
  onClose: () => void;
  floating?: boolean;
}) {
  const [person, setPerson] = useState<PersonPageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { locale } = useLocale();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/person?canonical_key=${encodeURIComponent(canonicalKey)}`)
      .then((r) => r.json())
      .then((json) => setPerson(json.data ?? null))
      .catch(() => setPerson(null))
      .finally(() => setLoading(false));
  }, [canonicalKey]);

  return (
    <div
      className={`${floating ? 'absolute right-3 top-3 z-10' : 'w-full'} w-72 rounded-xl border border-border bg-card shadow-lg`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
            <User size={14} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold line-clamp-1">
              {loading
                ? '…'
                : (person?.entity_name ?? t('network.person', 'Person'))}
            </div>
            {!loading && person && (person.date_of_birth || person.city) && (
              <div className="flex flex-wrap gap-x-2 gap-y-0">
                {person.date_of_birth && (
                  <span className="text-[10px] text-muted-foreground">
                    {t('network.born', 'Born')}: {person.date_of_birth}
                  </span>
                )}
                {person.city && (
                  <span className="text-[10px] text-muted-foreground">
                    {t('network.city', 'City')}: {person.city}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
        >
          <X size={16} />
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        {loading && (
          <p className="text-xs text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        )}
        {!loading && person && person.companies.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('network.connections', 'Connections')} (
              {person.companies.length})
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {person.companies.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 rounded-lg bg-muted/40 px-2.5 py-1.5 min-w-0"
                >
                  <div className="min-w-0">
                    {c.register_court ? (
                      <LocaleLink
                        href={generateCompanySlug(
                          {
                            name: c.company_name,
                            registerCourt: c.register_court,
                            registerType: c.register_type ?? undefined,
                            registerId: c.register_id ?? undefined,
                          },
                          locale,
                        )}
                        className="text-xs font-medium hover:underline text-foreground line-clamp-1"
                      >
                        {c.company_name}
                      </LocaleLink>
                    ) : (
                      <span className="text-xs font-medium text-foreground line-clamp-1">
                        {c.company_name}
                      </span>
                    )}
                    <div className="text-[10px] text-muted-foreground">
                      {c.role}
                    </div>
                  </div>
                  {c.share_percent != null && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {formatSharePercent(c.share_percent)}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simple info panel for nodes without eu_id / canonical_key
// ---------------------------------------------------------------------------
function NodeInfoPanel({
  label,
  nodeType,
  onClose,
  floating = true,
}: {
  label: string;
  nodeType: string;
  onClose: () => void;
  floating?: boolean;
}) {
  const isPerson = nodeType === 'person';
  return (
    <div
      className={`${floating ? 'absolute right-3 top-3 z-10' : 'w-full'} w-72 rounded-xl border border-border bg-card shadow-lg`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${isPerson ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
          >
            {isPerson ? <User size={14} /> : <Building2 size={14} />}
          </div>
          <span className="text-sm font-semibold line-clamp-2">{label}</span>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
        >
          <X size={16} />
        </button>
      </div>
      <div className="px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {isPerson ? 'Person' : 'Unternehmen'}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edge role filter panel
// ---------------------------------------------------------------------------
// Map a (German) backend role string to a stable i18n slug. Substring-matched,
// most-specific first (e.g. "Persönlich haftende(r) Gesellschafter(in)" must
// resolve to generalPartner, not shareholder). Gendered forms like "(in)" are
// handled automatically since we match on the stem.
const ROLE_SLUGS: [RegExp, string][] = [
  [/persönlich haftend|persoenlich haftend|komplement/i, 'generalPartner'],
  [/kommanditist/i, 'limitedPartner'],
  [/aktion[äa]r|anteilseigner|gesellschafter/i, 'shareholder'],
  [/inhaber/i, 'owner'],
  [/vorstandsvorsitzend/i, 'chairman'],
  [/notvorstand/i, 'emergencyBoardMember'],
  [/vorstand/i, 'managementBoard'],
  [/gesch[äa]ftsf[üu]hr|notgesch[äa]ftsf[üu]hr/i, 'managingDirector'],
  [/prokur/i, 'authorizedSignatory'],
  [/aufsichtsrat/i, 'supervisoryBoard'],
  [/verwaltungsrat/i, 'administrativeBoard'],
  [/liquidator|abwickler/i, 'liquidator'],
  [/insolvenzverwalter/i, 'insolvencyAdministrator'],
  [/konkursverwalter/i, 'bankruptcyAdministrator'],
  [/treuh[äa]nder/i, 'trustee'],
  [/muttergesellschaft|konzernmutter|holding/i, 'parentCompany'],
  [/tochter/i, 'subsidiary'],
  [/zweigniederlassung/i, 'branch'],
  [/hauptniederlassung/i, 'headOffice'],
  [/gr[üu]nder/i, 'founder'],
  [/gesch[äa]ftsleiter/i, 'businessManager'],
  [/betriebsleiter/i, 'operationsManager'],
  [/werkleiter/i, 'plantManager'],
  [/direktor/i, 'director'],
  [/b[üu]rgermeister/i, 'mayor'],
  [/partner/i, 'partner'],
];
function roleSlug(role: string): string | null {
  for (const [re, slug] of ROLE_SLUGS) if (re.test(role)) return slug;
  return null;
}

function EdgeFilterPanel({
  roles,
  disabledRoles,
  onToggle,
  showSubsidiaryManagers,
  onToggleSubsidiaryManagers,
  subsidiaryManagersLabel,
}: {
  roles: string[];
  disabledRoles: Set<string>;
  onToggle: (role: string) => void;
  showSubsidiaryManagers: boolean;
  onToggleSubsidiaryManagers: (next: boolean) => void;
  subsidiaryManagersLabel: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // The backend role strings are German. In non-German locales translate the
  // ones we know (i18n keys under network.roles); otherwise keep the raw role.
  const roleLabel = (role: string): string => {
    if ((locale ?? '').startsWith('de')) return role;
    const slug = roleSlug(role);
    return slug ? t(`network.roles.${slug}`, { defaultValue: role }) : role;
  };
  return (
    <div className="absolute top-2 left-2 z-10 max-h-[calc(100%-16px)] w-44 overflow-y-auto rounded-lg border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur-sm">
      <label className="flex items-start gap-2 rounded px-1.5 py-1 text-[11px] text-gray-700 hover:bg-gray-50 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={showSubsidiaryManagers}
          onChange={(e) => onToggleSubsidiaryManagers(e.target.checked)}
          className="mt-0.5 h-3 w-3 shrink-0 rounded border-gray-300 accent-blue-500"
        />
        <span className="leading-tight">{subsidiaryManagersLabel}</span>
      </label>
      {roles.map((role) => (
        <label
          key={role}
          className="flex items-start gap-2 rounded px-1.5 py-1 text-[11px] text-gray-700 hover:bg-gray-50 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={!disabledRoles.has(role)}
            onChange={() => onToggle(role)}
            className="mt-0.5 h-3 w-3 shrink-0 rounded border-gray-300 accent-blue-500"
          />
          <span className="leading-tight">{roleLabel(role)}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface CompanyNetworkDiagramProps {
  euId: string;
  companyName?: string;
  initialData?: CompanyNetworkResponse | null;
  focalFaviconUrl?: string | null;
  /**
   * Skip the role-based edge-direction normalisation. Use when the caller has
   * already oriented edges (e.g. the homepage demo flips management edges so
   * they place persons below the focal company).
   */
  disableEdgeNormalization?: boolean;
  /**
   * Bump to make the diagram refetch its network data — e.g. after the
   * backend finishes parsing the Gesellschafterliste, so newly parsed
   * shareholders appear without a page reload.
   */
  reloadToken?: number;
}

export function CompanyNetworkDiagram({
  euId,
  companyName,
  initialData,
  focalFaviconUrl,
  disableEdgeNormalization = false,
  reloadToken,
}: CompanyNetworkDiagramProps) {
  const { t } = useTranslation();
  const [networkData, setNetworkData] = useState<CompanyNetworkResponse | null>(
    initialData ?? null,
  );
  const [error, setError] = useState(false);
  /** Monotonic layout-run counter — surfaces re-layout loops in slow-op beacons. */
  const layoutSeqRef = useRef(0);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [layoutReady, setLayoutReady] = useState(false);
  // Role filter: all roles from the full layout, and which ones are currently enabled
  const [allRoleTypes, setAllRoleTypes] = useState<string[]>([]);
  const [disabledRoles, setDisabledRoles] = useState<Set<string>>(new Set());
  const [selectedPersonKey, setSelectedPersonKey] = useState<string | null>(
    null,
  );
  const [selectedCompany, setSelectedCompany] = useState<{
    euId: string;
    label: string;
    registerCourt?: string;
    registerType?: string;
    registerId?: string;
  } | null>(null);
  const [selectedNodeInfo, setSelectedNodeInfo] = useState<{
    label: string;
    nodeType: string;
  } | null>(null);
  const [highlightNodeId, setHighlightNodeId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // User override for the depth slider. undefined = follow the computed default
  // (autoDepth below); null = explicit "show all"; a number = that many hops.
  const [maxDepthOverride, setMaxDepthOverride] = useState<
    number | null | undefined
  >(undefined);
  // Default depth computed from the actually-displayed node set in the layout
  // effect (null = show all). Surfaced here so the slider can render it.
  const [autoDepth, setAutoDepth] = useState<number | null>(null);
  // The network data the default depth was last computed for. The default is
  // only (re)computed when the data itself changes (e.g. opening fullscreen
  // reloads it) — not when toggles/filters change the visible node count.
  const depthComputedForData = useRef<CompanyNetworkResponse | null>(null);
  // Max number of companies shown per hub in the fullscreen view — caps both
  // the focal company's subsidiaries and each person's other company mandates.
  // null = unlimited; defaults to 6 per hub.
  const [maxBranchCompanies, setMaxBranchCompanies] = useState<number | null>(
    6,
  );
  // Max number of upstream owners (shareholders) shown per company in the
  // fullscreen view. null = unlimited; defaults to 6 per company.
  const [maxOwners, setMaxOwners] = useState<number | null>(6);
  // Whether to show the Geschäftsführer (managers) of subsidiaries (Tochter-
  // firmen) in the fullscreen view. Off by default to declutter.
  const [showSubsidiaryManagers, setShowSubsidiaryManagers] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<ReactFlowInstance | null>(null);
  const trackNetworkClick = useCallback(
    (payload?: {
      subEvent?: 'fullscreen_open' | 'fullscreen_close' | 'filter_toggle';
      role?: string;
      checked?: boolean;
      source?: 'inline_overlay' | 'fullscreen_button' | 'filter_panel';
    }) => {
      fetch(`/api/companies/${euId}/network/click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload ?? {}),
      }).catch(() => {});
    },
    [euId],
  );

  const handleFullscreen = useCallback(
    (source?: 'inline_overlay' | 'fullscreen_button') => {
      setIsFullscreen((prev) => {
        const next = !prev;
        freezeBreadcrumb('fullscreen-toggle', { open: next });
        // Lock/unlock body scroll for overlay fullscreen
        document.body.style.overflow = next ? 'hidden' : '';
        trackNetworkClick({
          subEvent: next ? 'fullscreen_open' : 'fullscreen_close',
          source: next ? source : undefined,
        });
        return next;
      });
    },
    [trackNetworkClick],
  );

  // Clean up body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen, handleFullscreen]);

  // Re-fetch with more/fewer nodes when toggling fullscreen
  const hasToggledFullscreen = useRef(false);
  useEffect(() => {
    // Skip the initial mount — data is already loaded via initialData or the fetch below
    if (!hasToggledFullscreen.current) {
      hasToggledFullscreen.current = true;
      return;
    }
    const maxNodes = isFullscreen ? 100 : 30;
    freezeBreadcrumb('network-refetch-start', { maxNodes });
    fetch(`/api/companies/${euId}/network?max_nodes=${maxNodes}`)
      .then((r) => r.json())
      .then((json) => {
        freezeBreadcrumb('network-refetch-done', {
          nodes: json.data?.nodes?.length,
          edges: json.data?.edges?.length,
        });
        setNetworkData(json.data ?? null);
      })
      .catch(() => {});
  }, [isFullscreen, euId]);

  // Refetch when the caller bumps reloadToken (backend finished a
  // Gesellschafterliste parse — the graph may now contain more shareholders).
  // Compared via ref so euId/isFullscreen changes alone never refetch here.
  const lastReloadToken = useRef(reloadToken);
  useEffect(() => {
    if (reloadToken === undefined || reloadToken === lastReloadToken.current)
      return;
    lastReloadToken.current = reloadToken;
    const maxNodes = isFullscreen ? 100 : 30;
    freezeBreadcrumb('network-reload', { reloadToken, maxNodes });
    fetch(`/api/companies/${euId}/network?max_nodes=${maxNodes}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.data) setNetworkData(json.data);
      })
      .catch(() => {});
  }, [reloadToken, euId, isFullscreen]);

  // Update available role types whenever networkData changes
  useEffect(() => {
    if (!networkData) return;
    const roles = Array.from(
      new Set(networkData.edges.map((e) => e.role).filter(Boolean)),
    ).sort();
    setAllRoleTypes((prev) =>
      prev.length === roles.length && prev.every((r, i) => r === roles[i])
        ? prev
        : roles,
    );
    // Hide Prokuristen by default. Keep the previous Set identity when the
    // contents are unchanged — this effect runs on every networkData refetch,
    // and a fresh-but-equal Set is a dep of the layout effect, causing a
    // duplicate full relayout per fetch.
    const defaultDisabled = new Set(roles.filter((r) => /^prokur/i.test(r)));
    setDisabledRoles((prev) =>
      prev.size === defaultDisabled.size &&
      [...defaultDisabled].every((r) => prev.has(r))
        ? prev
        : defaultDisabled,
    );
  }, [networkData]);

  // Reset the depth override when the focal company changes so the new graph
  // starts from its own computed default.
  useEffect(() => {
    setMaxDepthOverride(undefined);
  }, [euId]);

  // Effective depth for the slider: the user's override if set, else the
  // default computed from the displayed node set (see the layout effect).
  const maxDepth =
    maxDepthOverride === undefined ? autoDepth : maxDepthOverride;

  // Network data with same-name company nodes merged (de-duplicated). Used as
  // the base for layout and all slider bounds.
  const dedupedData = useMemo(
    () => (networkData ? mergeSameNameNodes(networkData) : null),
    [networkData],
  );

  // Run ELK layout whenever networkData, fullscreen mode, or role filter changes
  useEffect(() => {
    if (!dedupedData) return;
    const data = dedupedData;
    setLayoutReady(false);

    // Freeze forensics: the graph filtering below runs synchronously on the
    // main thread; the ELK layered layout runs in a Web Worker (see
    // lib/elk-layout.ts) unless the worker fails and it falls back to the
    // main thread. Both scale with the network size, which varies wildly per
    // company. Breadcrumbs mark the phase; slow runs are beaconed so Grafana
    // shows which companies (and node/edge counts) are affected.
    // See lib/freeze-reporter.tsx.
    const layoutT0 = performance.now();
    const seq = ++layoutSeqRef.current;
    freezeBreadcrumb('network-layout-start', {
      euId: data.eu_id,
      seq,
      nodes: data.nodes.length,
      edges: data.edges.length,
      isFullscreen,
    });

    // If roles are disabled, pre-filter the network data before layout
    let filteredData = data;
    if (disabledRoles.size > 0) {
      const filteredEdges = data.edges.filter(
        (e) => !e.role || !disabledRoles.has(e.role),
      );
      // BFS from center to find all nodes reachable via remaining edges
      const adj = new Map<string, string[]>();
      for (const e of filteredEdges) {
        if (!adj.has(e.source)) adj.set(e.source, []);
        if (!adj.has(e.target)) adj.set(e.target, []);
        adj.get(e.source)!.push(e.target);
        adj.get(e.target)!.push(e.source);
      }
      const connectedIds = new Set<string>([data.eu_id]);
      const queue = [data.eu_id];
      while (queue.length) {
        const cur = queue.shift()!;
        for (const nb of adj.get(cur) ?? []) {
          if (!connectedIds.has(nb)) {
            connectedIds.add(nb);
            queue.push(nb);
          }
        }
      }
      filteredData = {
        ...data,
        nodes: data.nodes.filter((n) => connectedIds.has(n.id)),
        edges: filteredEdges,
      };
    }

    // Optionally hide the Geschäftsführer (managers) of subsidiaries: people
    // who only manage companies below the target (a daughter's CEO). A person
    // is KEPT if they manage the target or any parent of it, or if they own
    // anything; hidden only if every company they manage is a subsidiary.
    if (isFullscreen && !showSubsidiaryManagers) {
      const typeById = new Map(
        filteredData.nodes.map((n) => [n.id, n.node_type]),
      );
      // Keep-anchors: the focal plus every COMPANY above it — its owners, its
      // general partners (Komplementäre / persönlich haftende Gesellschafter),
      // and any company it is a Tochter of — walked upward. Each of these sits
      // above its child in the layout, so its managers are parent managers.
      const parentsByChild = new Map<string, string[]>();
      for (const e of filteredData.edges) {
        let child: string, parent: string;
        if (isSubsidiaryRole(e.role)) {
          child = e.source; // subsidiary
          parent = e.target; // parent
        } else if (isUpstreamRole(e.role) || isManagingRole(e.role)) {
          child = e.target; // owned / managed
          parent = e.source; // owner / general partner
        } else continue;
        if (typeById.get(parent) === 'person') continue; // company parents only
        if (!parentsByChild.has(child)) parentsByChild.set(child, []);
        parentsByChild.get(child)!.push(parent);
      }
      const keepAnchors = new Set<string>([data.eu_id]);
      const pq = [data.eu_id];
      while (pq.length) {
        const cur = pq.shift()!;
        for (const p of parentsByChild.get(cur) ?? []) {
          if (!keepAnchors.has(p)) {
            keepAnchors.add(p);
            pq.push(p);
          }
        }
      }
      const managesAnchor = new Set<string>(); // manages the target or a parent
      const managesAny = new Set<string>(); // manages some company
      const owns = new Set<string>();
      for (const e of filteredData.edges) {
        let person: string | undefined, company: string | undefined;
        if (
          typeById.get(e.source) === 'person' &&
          typeById.get(e.target) !== 'person'
        ) {
          person = e.source;
          company = e.target;
        } else if (
          typeById.get(e.target) === 'person' &&
          typeById.get(e.source) !== 'person'
        ) {
          person = e.target;
          company = e.source;
        } else continue;
        if (isUpstreamRole(e.role)) owns.add(person);
        if (isManagingRole(e.role)) {
          managesAny.add(person);
          if (keepAnchors.has(company)) managesAnchor.add(person);
        }
      }
      // Hide a person only if they manage at least one company, none of which is
      // the target or a parent, and they own nothing.
      const dropPersons = new Set<string>();
      for (const n of filteredData.nodes) {
        if (typeById.get(n.id) !== 'person') continue;
        if (owns.has(n.id) || managesAnchor.has(n.id)) continue;
        if (managesAny.has(n.id)) dropPersons.add(n.id);
      }
      if (dropPersons.size > 0) {
        const keptEdges = filteredData.edges.filter(
          (e) => !dropPersons.has(e.source) && !dropPersons.has(e.target),
        );
        // Prune anything orphaned from the focal by the removal.
        const adj = new Map<string, string[]>();
        for (const e of keptEdges) {
          if (!adj.has(e.source)) adj.set(e.source, []);
          if (!adj.has(e.target)) adj.set(e.target, []);
          adj.get(e.source)!.push(e.target);
          adj.get(e.target)!.push(e.source);
        }
        const reachable = new Set<string>([data.eu_id]);
        const queue = [data.eu_id];
        while (queue.length) {
          const cur = queue.shift()!;
          for (const nb of adj.get(cur) ?? []) {
            if (!reachable.has(nb)) {
              reachable.add(nb);
              queue.push(nb);
            }
          }
        }
        filteredData = {
          ...filteredData,
          nodes: filteredData.nodes.filter(
            (n) => !dropPersons.has(n.id) && reachable.has(n.id),
          ),
          edges: keptEdges.filter(
            (e) => reachable.has(e.source) && reachable.has(e.target),
          ),
        };
      }
    }

    // Fullscreen owner cap: per company keep at most `maxOwners` upstream
    // owners (shareholders). When every owner's share is known keep the largest
    // stakes; otherwise fall back to the most-connected. An owner kept by ANY
    // company it owns survives; orphans are then pruned from the focal.
    if (isFullscreen && maxOwners != null) {
      const groups = companyOwnerGroups(filteredData.edges);
      const degree = new Map<string, number>();
      for (const e of filteredData.edges) {
        degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
        degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
      }
      // Aggregate nodes (e.g. "+N weitere Kommanditist:innen") already summarise
      // the owners the backend folded away — never let the per-company owner cap
      // drop them, otherwise the "there are more" signal disappears.
      const aggregateIds = new Set(
        filteredData.nodes
          .filter((n) => n.node_type === 'aggregate')
          .map((n) => n.id),
      );
      const candidates = new Set<string>();
      const retained = new Set<string>();
      for (const g of groups) {
        for (const o of g.owners) candidates.add(o.id);
        const allKnown = g.owners.every((o) => o.share != null);
        const ranked = allKnown
          ? [...g.owners].sort((a, b) => (b.share ?? 0) - (a.share ?? 0))
          : [...g.owners].sort(
              (a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0),
            );
        ranked.slice(0, maxOwners).forEach((o) => retained.add(o.id));
      }
      const drop = new Set(
        [...candidates].filter(
          (id) => !retained.has(id) && !aggregateIds.has(id),
        ),
      );
      if (drop.size > 0) {
        const prunedEdges = filteredData.edges.filter(
          (e) => !drop.has(e.source) && !drop.has(e.target),
        );
        const adj = new Map<string, string[]>();
        for (const e of prunedEdges) {
          if (!adj.has(e.source)) adj.set(e.source, []);
          if (!adj.has(e.target)) adj.set(e.target, []);
          adj.get(e.source)!.push(e.target);
          adj.get(e.target)!.push(e.source);
        }
        const reachable = new Set<string>([data.eu_id]);
        const queue = [data.eu_id];
        while (queue.length) {
          const cur = queue.shift()!;
          for (const nb of adj.get(cur) ?? []) {
            if (!reachable.has(nb)) {
              reachable.add(nb);
              queue.push(nb);
            }
          }
        }
        filteredData = {
          ...filteredData,
          nodes: filteredData.nodes.filter((n) => reachable.has(n.id)),
          edges: prunedEdges.filter(
            (e) => reachable.has(e.source) && reachable.has(e.target),
          ),
        };
      }
    }

    // The full UBO chain above the focal (parent of parents of parents) is
    // always shown — it's exempt from both the per-hub and depth caps below.
    const uboChain = isFullscreen
      ? uboChainIds(filteredData.edges, data.eu_id)
      : new Set<string>();

    // Fullscreen per-hub company cap: for each hub (the focal company's
    // subsidiaries, and every person's other company mandates) keep at most
    // `maxBranchCompanies` companies — the highest-degree ones survive. A
    // company kept by ANY hub it belongs to stays. Orphaned descendants are
    // then pruned by a connectivity walk from the focal.
    if (isFullscreen && maxBranchCompanies != null) {
      const groups = companyBranchGroups(
        filteredData.nodes,
        filteredData.edges,
        data.eu_id,
      );
      const degree = new Map<string, number>();
      for (const e of filteredData.edges) {
        degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
        degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
      }
      const branchIds = new Set<string>();
      const retained = new Set<string>();
      for (const g of groups) {
        for (const id of g.companyIds) branchIds.add(id);
        [...g.companyIds]
          .sort((a, b) => (degree.get(b) ?? 0) - (degree.get(a) ?? 0))
          .slice(0, maxBranchCompanies)
          .forEach((id) => retained.add(id));
      }
      const drop = new Set(
        [...branchIds].filter((id) => !retained.has(id) && !uboChain.has(id)),
      );
      if (drop.size > 0) {
        const prunedEdges = filteredData.edges.filter(
          (e) => !drop.has(e.source) && !drop.has(e.target),
        );
        // Connectivity prune from the focal over the remaining edges.
        const adj = new Map<string, string[]>();
        for (const e of prunedEdges) {
          if (!adj.has(e.source)) adj.set(e.source, []);
          if (!adj.has(e.target)) adj.set(e.target, []);
          adj.get(e.source)!.push(e.target);
          adj.get(e.target)!.push(e.source);
        }
        const reachable = new Set<string>([data.eu_id]);
        const queue = [data.eu_id];
        while (queue.length) {
          const cur = queue.shift()!;
          for (const nb of adj.get(cur) ?? []) {
            if (!reachable.has(nb)) {
              reachable.add(nb);
              queue.push(nb);
            }
          }
        }
        filteredData = {
          ...filteredData,
          nodes: filteredData.nodes.filter((n) => reachable.has(n.id)),
          edges: prunedEdges.filter(
            (e) => reachable.has(e.source) && reachable.has(e.target),
          ),
        };
      }
    }

    // Depth to apply: the user's override, or — when they haven't touched the
    // slider — a default derived from the actually-displayed node set (show all
    // when < 30 nodes, else the depth closest to 30 visible nodes). The default
    // is computed only when the data changes (e.g. the initial fullscreen load),
    // NOT on every toggle/filter — so toggling things afterwards never shifts
    // the depth even if the visible count crosses 30.
    let effectiveDepth: number | null = null;
    if (isFullscreen) {
      if (maxDepthOverride !== undefined) {
        effectiveDepth = maxDepthOverride;
      } else if (depthComputedForData.current !== dedupedData) {
        effectiveDepth = defaultDepthForSet(
          filteredData.nodes,
          filteredData.edges,
          uboChain,
          data.eu_id,
        );
        depthComputedForData.current = dedupedData;
        setAutoDepth((prev) =>
          prev === effectiveDepth ? prev : effectiveDepth,
        );
      } else {
        effectiveDepth = autoDepth;
      }
    }

    // Fullscreen depth cap: keep only nodes within `effectiveDepth` hops
    // (undirected) of the focal. null means "show everything". UBO-chain nodes
    // are always kept.
    if (isFullscreen && effectiveDepth != null) {
      const cap = effectiveDepth;
      const adj = new Map<string, string[]>();
      for (const e of filteredData.edges) {
        if (!adj.has(e.source)) adj.set(e.source, []);
        if (!adj.has(e.target)) adj.set(e.target, []);
        adj.get(e.source)!.push(e.target);
        adj.get(e.target)!.push(e.source);
      }
      const depth = new Map<string, number>([[data.eu_id, 0]]);
      const queue = [data.eu_id];
      while (queue.length) {
        const cur = queue.shift()!;
        const d = depth.get(cur)!;
        if (d >= cap) continue;
        for (const nb of adj.get(cur) ?? []) {
          if (!depth.has(nb)) {
            depth.set(nb, d + 1);
            queue.push(nb);
          }
        }
      }
      const keep = (id: string) => depth.has(id) || uboChain.has(id);
      filteredData = {
        ...filteredData,
        nodes: filteredData.nodes.filter((n) => keep(n.id)),
        edges: filteredData.edges.filter(
          (e) => keep(e.source) && keep(e.target),
        ),
      };
    }

    const preprocessMs = Math.round(performance.now() - layoutT0);
    freezeBreadcrumb('network-elk-start', {
      euId: data.eu_id,
      filteredNodes: filteredData.nodes.length,
      filteredEdges: filteredData.edges.length,
      preprocessMs,
    });

    getElkLayout(
      filteredData,
      !isFullscreen,
      isMobile,
      disableEdgeNormalization,
    )
      .then(({ nodes: n, edges: e }) => {
        const totalMs = Math.round(performance.now() - layoutT0);
        freezeBreadcrumb('network-layout-done', { euId: data.eu_id, totalMs });
        if (totalMs > 500) {
          reportFreezeEvent('slow-op', {
            durationMs: totalMs,
            op: {
              name: 'network-layout',
              euId: data.eu_id,
              seq,
              mode: elkLastMode(),
              rawNodes: data.nodes.length,
              rawEdges: data.edges.length,
              filteredNodes: filteredData.nodes.length,
              filteredEdges: filteredData.edges.length,
              preprocessMs,
              isFullscreen,
            },
          });
        }
        // Inject focal company favicon into the center node
        const enriched = focalFaviconUrl
          ? n.map((node) =>
              node.type === 'center'
                ? {
                    ...node,
                    data: { ...node.data, faviconUrl: focalFaviconUrl },
                  }
                : node,
            )
          : n;
        // Last crumb before handing off to React: if a dead tab's trail ends
        // here (no 'network-fitview' after), the freeze is in render/commit.
        freezeBreadcrumb('network-apply', {
          seq,
          nodes: enriched.length,
          edges: e.length,
          mode: elkLastMode(),
        });
        setNodes(enriched);
        setEdges(e);
        setLayoutReady(true);
      })
      .catch((err) => {
        freezeBreadcrumb('network-layout-error', {
          seq,
          error: err instanceof Error ? err.message : String(err),
        });
        setError(true);
      });
  }, [
    networkData,
    isFullscreen,
    isMobile,
    disabledRoles,
    focalFaviconUrl,
    euId,
    disableEdgeNormalization,
    maxDepthOverride,
    maxBranchCompanies,
    maxOwners,
    showSubsidiaryManagers,
    dedupedData,
  ]);

  const router = useRouter();

  // Deepest hop distance any node sits from the focal company — the upper bound
  // for the fullscreen depth slider.
  const maxDepthAvailable = useMemo(() => {
    if (!dedupedData) return 1;
    const adj = new Map<string, string[]>();
    for (const e of dedupedData.edges) {
      if (!adj.has(e.source)) adj.set(e.source, []);
      if (!adj.has(e.target)) adj.set(e.target, []);
      adj.get(e.source)!.push(e.target);
      adj.get(e.target)!.push(e.source);
    }
    const depth = new Map<string, number>([[dedupedData.eu_id, 0]]);
    const queue = [dedupedData.eu_id];
    let max = 0;
    while (queue.length) {
      const cur = queue.shift()!;
      const d = depth.get(cur)!;
      for (const nb of adj.get(cur) ?? []) {
        if (!depth.has(nb)) {
          depth.set(nb, d + 1);
          if (d + 1 > max) max = d + 1;
          queue.push(nb);
        }
      }
    }
    return Math.max(1, max);
  }, [networkData]);
  // null maxDepth = "show all" → render the thumb at the maximum.
  const depthValue = Math.min(maxDepth ?? maxDepthAvailable, maxDepthAvailable);

  // Largest company fan-out of any single hub (focal's subsidiaries or a
  // person's mandates) — the upper ("unlimited") bound for the slider, since
  // a cap at or above this leaves every hub untouched.
  const maxBranchGroupSize = useMemo(() => {
    if (!dedupedData) return 0;
    const groups = companyBranchGroups(
      dedupedData.nodes,
      dedupedData.edges,
      dedupedData.eu_id,
    );
    return groups.reduce((m, g) => Math.max(m, g.companyIds.length), 0);
  }, [dedupedData]);
  // null maxBranchCompanies = "unlimited" → render the thumb at the maximum.
  const branchValue = Math.min(
    maxBranchCompanies ?? maxBranchGroupSize,
    maxBranchGroupSize,
  );

  // Largest number of owners (shareholders) any single company has — the upper
  // ("unlimited") bound for the owners slider.
  const maxOwnerGroupSize = useMemo(() => {
    if (!dedupedData) return 0;
    const groups = companyOwnerGroups(dedupedData.edges);
    return groups.reduce((m, g) => Math.max(m, g.owners.length), 0);
  }, [dedupedData]);
  // null maxOwners = "unlimited" → render the thumb at the maximum.
  const ownerValue = Math.min(
    maxOwners ?? maxOwnerGroupSize,
    maxOwnerGroupSize,
  );

  const displayEdges = useMemo(() => {
    // Light-only warm-cream palette to match the homepage demo. fd-line-2 is
    // #d6d3c8; markers can't resolve CSS vars so we use the literal here.
    const baseColor = '#d6d3c8';
    // Slate-blue sits between the warm-rust upstream ink and the warm-gray
    // downstream so a merged (Gesellschafter + Geschäftsführer) edge reads as
    // its own category at a glance.
    const mergedColor = '#5a7d9c';
    const isUpstream = (e: Edge): boolean =>
      isUpstreamRole(e.data?.role as string | undefined);
    const isMerged = (e: Edge): boolean => !!e.data?.isMerged;
    const upstreamStyle = {
      stroke: 'var(--fd-accent-ink)',
      strokeWidth: 1.6,
      strokeDasharray: '7 4',
    } as React.CSSProperties;
    const mergedStyle = {
      stroke: mergedColor,
      strokeWidth: 1.6,
      strokeDasharray: '6 3 1 3',
    } as React.CSSProperties;
    if (!highlightNodeId) {
      return edges.map((e) => {
        const merged = isMerged(e);
        const upstream = isUpstream(e);
        const style = merged
          ? mergedStyle
          : upstream
            ? upstreamStyle
            : ({ ...e.style, stroke: baseColor } as React.CSSProperties);
        return {
          ...e,
          className: merged
            ? 'is-merged-edge'
            : upstream
              ? 'is-upstream-edge'
              : undefined,
          style,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 16,
            height: 16,
            color: merged ? mergedColor : baseColor,
          },
        };
      });
    }
    return edges.map((e) => {
      const connected =
        e.source === highlightNodeId || e.target === highlightNodeId;
      const merged = isMerged(e);
      const upstream = isUpstream(e);
      const dimStyle: React.CSSProperties = merged
        ? { ...mergedStyle, opacity: 0.15 }
        : upstream
          ? { ...upstreamStyle, opacity: 0.15 }
          : { stroke: baseColor, strokeWidth: 1, opacity: 0.15 };
      const activeStyle: React.CSSProperties = merged
        ? { ...mergedStyle, stroke: '#3b82f6', strokeWidth: 1.8 }
        : upstream
          ? { ...upstreamStyle, stroke: '#3b82f6', strokeWidth: 1.8 }
          : { stroke: '#3b82f6', strokeWidth: 2.5 };
      return {
        ...e,
        className: merged
          ? 'is-merged-edge'
          : upstream
            ? 'is-upstream-edge'
            : undefined,
        style: connected ? activeStyle : dimStyle,
        markerEnd: connected
          ? {
              type: MarkerType.ArrowClosed,
              width: 16,
              height: 16,
              color: '#3b82f6',
            }
          : {
              type: MarkerType.ArrowClosed,
              width: 16,
              height: 16,
              color: merged ? mergedColor : baseColor,
            },
        zIndex: connected ? 1 : 0,
      };
    });
  }, [edges, highlightNodeId]);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    // Toggle highlight for any node type
    setHighlightNodeId((prev) => (prev === node.id ? null : node.id));
    // Reset all panels
    setSelectedCompany(null);
    setSelectedPersonKey(null);
    setSelectedNodeInfo(null);
    // Show the appropriate detail panel
    const nodeType = node.data.nodeType as string;
    if (nodeType === 'person' && node.data.canonical_key) {
      setSelectedPersonKey(node.data.canonical_key as string);
    } else if (
      (nodeType === 'company' || nodeType === 'center') &&
      node.data.eu_id
    ) {
      setSelectedCompany({
        euId: node.data.eu_id as string,
        label: node.data.label as string,
        registerCourt: node.data.register_court as string | undefined,
        registerType: node.data.register_type as string | undefined,
        registerId: node.data.register_id as string | undefined,
      });
    } else {
      // Fallback: show simple info panel for nodes without identifiers
      setSelectedNodeInfo({ label: node.data.label as string, nodeType });
    }
  }, []);

  const handlePaneClick = useCallback(() => {
    setHighlightNodeId(null);
    setSelectedPersonKey(null);
    setSelectedCompany(null);
    setSelectedNodeInfo(null);
  }, []);

  const handleInlinePaneClick = useCallback(() => {
    handleFullscreen('inline_overlay');
  }, [handleFullscreen]);

  const handleRoleToggle = useCallback(
    (role: string) => {
      setDisabledRoles((prev) => {
        const next = new Set(prev);
        const checked = next.has(role);
        if (checked) next.delete(role);
        else next.add(role);
        trackNetworkClick({
          subEvent: 'filter_toggle',
          role,
          checked,
          source: 'filter_panel',
        });
        return next;
      });
    },
    [trackNetworkClick],
  );

  const handleInit = useCallback((instance: ReactFlowInstance) => {
    freezeBreadcrumb('flow-init');
    flowRef.current = instance;
    // Nodes already have ELK positions — fit immediately on mount
    instance.fitView({ padding: 0, maxZoom: 0.8, duration: 0 });
  }, []);

  const fullscreenFlowRef = useRef<ReactFlowInstance | null>(null);
  const handleFullscreenInit = useCallback((instance: ReactFlowInstance) => {
    freezeBreadcrumb('flow-fullscreen-init');
    fullscreenFlowRef.current = instance;
    instance.fitView({ padding: 0.08, duration: 0 });
  }, []);

  // fitView after layout recomputes (e.g. fullscreen toggle fetches new data)
  useEffect(() => {
    if (!layoutReady) return;
    // Short timeout lets ReactFlow process the new node set before fitView
    const id = setTimeout(() => {
      // Reaching this crumb proves React committed the new node set — the
      // 'network-apply' → 'network-fitview' gap brackets render/commit.
      freezeBreadcrumb('network-fitview', { nodes: nodes.length });
      flowRef.current?.fitView({ padding: 0, maxZoom: 0.8, duration: 0 });
      fullscreenFlowRef.current?.fitView({ padding: 0.08, duration: 0 });
      freezeBreadcrumb('network-fitview-done');
    }, 50);
    return () => clearTimeout(id);
  }, [layoutReady, nodes]);

  if (error || !networkData || nodes.length === 0) return null;

  const sharedFlowProps = {
    nodes,
    edges: displayEdges as Edge[],
    nodeTypes: NODE_TYPES,
    edgeTypes: EDGE_TYPES,
    onNodeClick: handleNodeClick,
    onPaneClick: handlePaneClick,
    minZoom: 0.05,
    maxZoom: 2,
    nodesConnectable: false,
    elementsSelectable: true,
    zoomOnDoubleClick: false,
    colorMode: 'light' as const,
    proOptions: { hideAttribution: true },
  } as const;

  return (
    <div className="fd-diagram">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          {t('network.title', 'Netzwerk')}
        </h2>
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
      </div>

      {!collapsed && (
        <>
          <style>{`.inline-network .react-flow__pane{cursor:zoom-in!important}.inline-network .react-flow__node{cursor:pointer!important}.react-flow{background:transparent!important}`}</style>
          <div
            ref={containerRef}
            data-nosnippet
            className="inline-network relative h-[200px] w-full overflow-hidden rounded-xl bg-transparent"
          >
            {!layoutReady && (
              <div className="absolute inset-0 z-20 animate-pulse rounded-xl bg-muted/40" />
            )}
            {!isFullscreen && (
              <ReactFlow
                {...sharedFlowProps}
                onInit={handleInit}
                onPaneClick={handleInlinePaneClick}
                nodesDraggable={false}
                panOnDrag={false}
                panOnScroll={false}
                zoomOnScroll={false}
                zoomOnPinch={false}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFullscreen('fullscreen_button');
                  }}
                  className="absolute bottom-2 left-2 z-10 flex h-8 w-8 items-center justify-center rounded bg-white/80 shadow hover:bg-white transition-colors"
                  title="Vollbild"
                >
                  <Maximize2 size={18} className="text-gray-600" />
                </button>
              </ReactFlow>
            )}
          </div>
        </>
      )}

      {/* CSS-based fullscreen overlay — works on all browsers including iOS Safari */}
      {isFullscreen && (
        <div
          data-nosnippet
          ref={(el) => {
            if (el) freezeBreadcrumb('fullscreen-overlay-dom');
          }}
          className="fixed inset-0 z-[9999] bg-background [&_.react-flow__node]:!cursor-pointer [&_.react-flow__edge]:!cursor-pointer"
          style={{ touchAction: 'manipulation' }}
        >
          {!layoutReady && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-background">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-300" />
            </div>
          )}
          <ReactFlow
            {...sharedFlowProps}
            onInit={handleFullscreenInit}
            onPaneClick={handlePaneClick}
            nodesDraggable={true}
            panOnDrag={true}
            panOnScroll={false}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <EdgeFilterPanel
              roles={allRoleTypes}
              disabledRoles={disabledRoles}
              onToggle={handleRoleToggle}
              showSubsidiaryManagers={showSubsidiaryManagers}
              onToggleSubsidiaryManagers={setShowSubsidiaryManagers}
              subsidiaryManagersLabel={t(
                'network.subsidiaryManagers',
                'Geschäftsführer der Tochterfirmen',
              )}
            />
            {(maxDepthAvailable > 1 ||
              maxBranchGroupSize > 5 ||
              maxOwnerGroupSize > 6) && (
              <div className="absolute bottom-3 left-3 z-10 w-56 space-y-3 rounded-lg border border-gray-200 bg-white/95 px-3 py-2.5 shadow-lg backdrop-blur-sm dark:border-zinc-700 dark:bg-zinc-800/95">
                {maxDepthAvailable > 1 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between text-[11px] font-medium text-gray-700 dark:text-gray-300">
                      <span>{t('network.depth', 'Verbindungstiefe')}</span>
                      <span className="tabular-nums text-gray-500 dark:text-gray-400">
                        {depthValue === maxDepthAvailable
                          ? t('network.depthAll', 'alle')
                          : t('network.depthHops', '{{count}} Schritte', {
                              count: depthValue,
                            })}{' '}
                        · {nodes.length}
                      </span>
                    </div>
                    <Slider
                      min={1}
                      max={maxDepthAvailable}
                      step={1}
                      value={[depthValue]}
                      onValueChange={(v) =>
                        setMaxDepthOverride(
                          v[0] >= maxDepthAvailable ? null : v[0],
                        )
                      }
                      aria-label={t('network.depth', 'Verbindungstiefe')}
                    >
                      <SliderThumb />
                    </Slider>
                  </div>
                )}
                {maxBranchGroupSize > 5 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between text-[11px] font-medium text-gray-700 dark:text-gray-300">
                      <span>
                        {t(
                          'network.companiesPerHub',
                          'Firmen je Person/Mutter',
                        )}
                      </span>
                      <span className="tabular-nums text-gray-500 dark:text-gray-400">
                        {branchValue >= maxBranchGroupSize
                          ? t('network.unlimited', 'unbegrenzt')
                          : branchValue}
                      </span>
                    </div>
                    <Slider
                      min={5}
                      max={maxBranchGroupSize}
                      step={1}
                      value={[branchValue]}
                      onValueChange={(v) =>
                        setMaxBranchCompanies(
                          v[0] >= maxBranchGroupSize ? null : v[0],
                        )
                      }
                      aria-label={t(
                        'network.companiesPerHub',
                        'Firmen je Person/Mutter',
                      )}
                    >
                      <SliderThumb />
                    </Slider>
                  </div>
                )}
                {maxOwnerGroupSize > 6 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between text-[11px] font-medium text-gray-700 dark:text-gray-300">
                      <span>
                        {t('network.ownersPerCompany', 'Eigentümer je Firma')}
                      </span>
                      <span className="tabular-nums text-gray-500 dark:text-gray-400">
                        {ownerValue >= maxOwnerGroupSize
                          ? t('network.unlimited', 'unbegrenzt')
                          : ownerValue}
                      </span>
                    </div>
                    <Slider
                      min={1}
                      max={maxOwnerGroupSize}
                      step={1}
                      value={[ownerValue]}
                      onValueChange={(v) =>
                        setMaxOwners(v[0] >= maxOwnerGroupSize ? null : v[0])
                      }
                      aria-label={t(
                        'network.ownersPerCompany',
                        'Eigentümer je Firma',
                      )}
                    >
                      <SliderThumb />
                    </Slider>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => handleFullscreen()}
              className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 dark:bg-zinc-800/90 shadow-md hover:bg-white dark:hover:bg-zinc-700 transition-colors"
              title="Schließen"
            >
              <X size={18} className="text-gray-700 dark:text-gray-300" />
            </button>
          </ReactFlow>
        </div>
      )}
    </div>
  );
}
